import { useState, useEffect, useRef } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useTheme } from "../contexts/ThemeContext";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Sparkles, Star, BookOpen, Music, Crown, LogOut, Menu, X, Mic, Headphones, BookText, Bell, Sun, Moon, PawPrint, Trophy, Salad, Target, Flame, Heart, Bed, Sunrise, QrCode, Palette, ChevronDown, ChevronRight, Compass, HandHeart, Leaf, BarChart3, TrendingUp, MessageSquare, Download, FileText, Share2, Users, Wind, Calendar, Apple, MessagesSquare, Smile, Stethoscope, CloudMoon, Hash, Volume2, TreeDeciduous, Activity, Settings } from "lucide-react";
import { toast } from "sonner";
import MoodTracker from "../components/MoodTracker";
import UpgradeModal from "../components/UpgradeModal";
import { JourneyUnlockPrompt, JourneyProgressCard, useJourneyProgress } from "../components/JourneyProgress";
import MerchCollabSection from "../components/MerchCollabSection";
import SoulConnectionQuiz from "../components/SoulConnectionQuiz";
import VibeOutHub from "../components/VibeOutHub";
import WeeklyProgressReport from "../components/WeeklyProgressReport";
import MoonCycleTracker from "../components/MoonCycleTracker";
import MiracleDeck from "../components/MiracleDeck";
import HealingTogether from "../components/HealingTogether";
import ManifestationCircles from "../components/ManifestationCircles";
import CollectiveIntention from "../components/CollectiveIntention";
import BirthdayCelebration from "../components/BirthdayCelebration";
import ForYouFeed from "../components/ForYouFeed";
// DailyStreak import removed (iter 98) — replaced by unified Daily Ritual Streaks card
import NotificationCenter from "../components/NotificationCenter";
import NotificationSettings from "../components/NotificationSettings";
import BreatheFirst from "../components/BreatheFirst";
import ExploreDrawer from "../components/ExploreDrawer";
import PushSubscribeNudge from "../components/PushSubscribeNudge";
import UserAvatar from "../components/UserAvatar";
import SoulWeather from "../components/SoulWeather";
import DailyConstellation from "../components/DailyConstellation";
import { getGenderEmoji } from "../utils/genderEmojis";
import { initializePushNotifications } from "../utils/pushNotifications";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Featured Black-Owned WELLNESS brands (Ships Nationwide/Online).
// Refreshes DAILY (was biweekly) — deterministic per user, so different users
// see different brands today and every brand gets visibility.
//
// Curation principle (per Lauren, April 2026): wellness-FIRST, not random
// Black brands. Lipstick / edge-control / glassware were dropped in favor of
// brands aligned with our actual brand promise — "the blend of daily living
// with spiritual practices". Every entry below answers some version of:
//   • body wellness  (supplements, herbs, adaptogens, sun protection)
//   • womb / feminine wellness  (Honey Pot, Goddess Detox, Sacred Mama, Mama Glow)
//   • ancestral / ritual care   (Nubian Heritage, Hanahana, 54 Thrones, Forvr Mood)
//   • mental wellness            (Topicals, Brown Girl Jane, Peak & Valley)
//   • men's wellness             (Bevel)
//   • intimate / sexual wellness (OURS)
//   • clean ingredient hair/body (Briogeo, Adwoa, BLK+GRN marketplace)
const FEATURED_BUSINESSES = [
  {
    name: "Golde",
    tagline: "Superfood wellness rituals",
    description: "Plant-based superfoods for radiant skin and a calm mind. Founded by Trinity Mouzon Wofford.",
    category: "Superfood Wellness",
    url: "https://www.golde.co",
    emoji: "✨",
    color: "from-amber-400 to-yellow-500",
    products: ["Matcha", "Turmeric Blends", "Mushroom Cacao"],
    isNewBrand: false,
    founder: "Trinity Mouzon Wofford"
  },
  {
    name: "Peak and Valley",
    tagline: "Adaptogenic herbal blends",
    description: "Herbal adaptogens and mushroom blends for stress relief, mood, and nervous-system balance.",
    category: "Mental Wellness",
    url: "https://www.peakandvalleywellness.com",
    emoji: "🧘🏾‍♀️",
    color: "from-violet-400 to-purple-500",
    products: ["Mushroom Blends", "Stress Relief", "Sleep Support"],
    isNewBrand: false,
    founder: "Nadine Joseph"
  },
  {
    name: "Brown Girl Jane",
    tagline: "Wellness elevated",
    description: "CBD-infused wellness products for balance, calm, and intentional self-care.",
    category: "CBD Wellness",
    url: "https://www.browngirljane.com",
    emoji: "🕯️",
    color: "from-purple-400 to-violet-500",
    products: ["CBD Oils", "Body Care", "Candles"],
    isNewBrand: false,
    founder: "Malaika Jones, Tai Beauchamp & Nia Jones"
  },
  {
    name: "Hanahana Beauty",
    tagline: "Shea butter self-care rituals",
    description: "Ethically-sourced shea butter skincare supporting women in Ghana. Slow, sacred body care.",
    category: "Ritual Body Care",
    url: "https://www.hanahanabeauty.com",
    emoji: "🌻",
    color: "from-yellow-400 to-amber-500",
    products: ["Shea Butter", "Body Oils", "Gift Sets"],
    isNewBrand: false,
    founder: "Abena Boamah-Acheampong"
  },
  {
    name: "Black Girl Vitamins",
    tagline: "Supplements made for us",
    description: "Vitamins and supplements specifically formulated for Black women's unique health needs.",
    category: "Supplements",
    url: "https://blackgirlvitamins.co",
    emoji: "🌿",
    color: "from-pink-400 to-rose-500",
    products: ["Multivitamins", "Hair Growth", "Immune Support"],
    isNewBrand: false,
    founder: "Maxine Smith-Thompson"
  },
  {
    name: "54 Thrones",
    tagline: "African beauty butters",
    description: "Luxury body care inspired by ancestral African beauty rituals from all 54 nations.",
    category: "Ancestral Body Care",
    url: "https://www.54thrones.com",
    emoji: "👑",
    color: "from-amber-500 to-orange-500",
    products: ["Beauty Butters", "Oils", "Gift Sets"],
    isNewBrand: false,
    founder: "Christina Funke Tegbe"
  },
  {
    name: "Black Girl Sunscreen",
    tagline: "Skin health for melanin",
    description: "Invisible sunscreen made specifically for women of color — sun protection without the white cast.",
    category: "Skin Health",
    url: "https://www.blackgirlsunscreen.com",
    emoji: "🌞",
    color: "from-orange-400 to-yellow-400",
    products: ["Classic SPF 30", "Kids SPF 50", "Make it Matte SPF 45"],
    isNewBrand: false,
    founder: "Shontay Lundy"
  },
  {
    name: "Topicals",
    tagline: "Skin meets mental health",
    description: "Science-backed skincare with a mental-health-forward approach. Normalizing skin struggles, gently.",
    category: "Mental-Health Skincare",
    url: "https://www.mytopicals.com",
    emoji: "💜",
    color: "from-teal-400 to-cyan-500",
    products: ["Faded Serum", "Like Butter Mask", "Slather Balm"],
    isNewBrand: false,
    founder: "Olamide Olowe"
  },
  {
    name: "The Honey Pot",
    tagline: "Plant-derived feminine wellness",
    description: "The first complete plant-based feminine care line. Clean ingredients for intimate wellness.",
    category: "Feminine Wellness",
    url: "https://www.thehoneypot.co",
    emoji: "🌺",
    color: "from-amber-400 to-orange-400",
    products: ["Washes", "Wipes", "Supplements"],
    isNewBrand: false,
    founder: "Beatrice Dixon"
  },
  {
    name: "Beneath Your Mask",
    tagline: "Healing-journey self-care",
    description: "Plant-based luxury body care born from one woman's healing journey through autoimmune illness.",
    category: "Healing Self-Care",
    url: "https://beneathyourmask.com",
    emoji: "🪷",
    color: "from-indigo-500 to-violet-600",
    products: ["Whipped Body Souffle", "Hair Mask", "Bath Soak"],
    isNewBrand: false,
    founder: "Dana Jackson"
  },
  {
    name: "BLK + GRN",
    tagline: "All-natural marketplace",
    description: "Curated marketplace of 100% toxic-free wellness products from Black artisans.",
    category: "Clean Living",
    url: "https://www.blkgrn.com",
    emoji: "🌿",
    color: "from-gray-700 to-gray-900",
    products: ["Skincare", "Home", "Wellness"],
    isNewBrand: false,
    founder: "Dr. Kristian Henderson"
  },
  {
    name: "Plant Mother",
    tagline: "Herbal teas + mood medicine",
    description: "Adaptogenic herbal blends formulated by a Black herbalist for mood, hormones, and ancestral connection.",
    category: "Herbal Apothecary",
    url: "https://www.plantmother.co",
    emoji: "🌱",
    color: "from-green-500 to-lime-600",
    products: ["Mood Tea", "Yoni Tea", "Tinctures"],
    isNewBrand: false,
    founder: "Plant Mother apothecary"
  },
  {
    name: "Bevel",
    tagline: "Grooming for melanated skin",
    description: "Clinical-grade grooming designed for Black men's skin and hair — a wellness ritual, not a chore.",
    category: "Men's Wellness",
    url: "https://getbevel.com",
    emoji: "✂️",
    color: "from-slate-700 to-slate-900",
    products: ["Safety Razor", "Shave Cream", "Trimmer"],
    isNewBrand: false,
    founder: "Tristan Walker"
  },
  {
    name: "OURS Wellness",
    tagline: "Plant-based intimate wellness",
    description: "Sexual wellness products centered on Black pleasure as healing — sustainable, plant-based, sacred.",
    category: "Intimate Wellness",
    url: "https://shopourswellness.com",
    emoji: "🌹",
    color: "from-fuchsia-500 to-pink-600",
    products: ["Body Oils", "Intimate Care", "Candles"],
    isNewBrand: false,
    founder: "Eden Bridgeman Sklenar"
  },
  {
    name: "Briogeo",
    tagline: "Clean haircare wellness",
    description: "Six-free, Black-women-owned hair care. Salon-grade nourishment for textured hair.",
    category: "Clean Haircare",
    url: "https://briogeohair.com",
    emoji: "💆🏾‍♀️",
    color: "from-emerald-400 to-teal-500",
    products: ["Don't Despair Repair", "Scalp Revival", "Curl Charisma"],
    isNewBrand: false,
    founder: "Nancy Twine"
  },
  {
    name: "Adwoa Beauty",
    tagline: "Curls, coils, and coconuts",
    description: "Clean haircare crafted for textured hair by a former Bumble & Bumble stylist. Baomint is a cult favorite.",
    category: "Clean Curl Care",
    url: "https://adwoabeauty.com",
    emoji: "🌀",
    color: "from-emerald-500 to-cyan-600",
    products: ["Baomint Leave-in", "Mint Hibiscus Mask", "Scalp Drops"],
    isNewBrand: false,
    founder: "Julian Addo"
  },
  {
    name: "The Lip Bar",
    tagline: "Bold beauty for all",
    description: "Vegan, cruelty-free cosmetics celebrating diverse beauty. From Shark Tank to nationwide retail success.",
    category: "Clean Cosmetics",
    url: "https://www.thelipbar.com",
    emoji: "🌸",
    color: "from-rose-500 to-pink-600",
    products: ["Lipsticks", "Foundation", "Setting Spray"],
    isNewBrand: false,
    founder: "Melissa Butler"
  },
  {
    name: "Endo and Sea",
    tagline: "CBD-infused calm",
    description: "Sparkling CBD-infused wellness drinks and tinctures designed to soothe stress and anxiety. Slow ritual in a can.",
    category: "CBD Wellness",
    url: "https://endoandsea.com",
    emoji: "🌊",
    color: "from-teal-500 to-cyan-600",
    products: ["Sparkling CBD Drinks", "Tinctures", "Wellness Bundles"],
    isNewBrand: true,
    founder: "Black-women co-founders"
  },
  {
    name: "Nubian Heritage",
    tagline: "Ancestral African body rituals",
    description: "Soaps, oils, and balms drawing on heritage botanicals — black soap, shea, raw honey, mango butter.",
    category: "Ancestral Apothecary",
    url: "https://www.nubianheritage.com",
    emoji: "🦁",
    color: "from-amber-700 to-orange-700",
    products: ["African Black Soap", "Raw Shea", "Body Oils"],
    isNewBrand: false,
    founder: "Richelieu Dennis & Nyema Tubman"
  },
  {
    name: "Goddess Detox",
    tagline: "Sacred yoni & womb care",
    description: "Yoni steams, herbal soaks, and sacred feminine wellness rooted in ancestral womb work.",
    category: "Womb Wellness",
    url: "https://goddessdetox.org",
    emoji: "🌷",
    color: "from-pink-500 to-rose-600",
    products: ["Yoni Steams", "Detox Pearls", "Womb Tea"],
    isNewBrand: false,
    founder: "Olanikee Osi"
  },
  {
    name: "Mama Glow",
    tagline: "Doula-led pregnancy wellness",
    description: "Maternal wellness platform for pregnancy, birth, and postpartum — doula-led, ancestral, Black-mom-centered.",
    category: "Maternal Wellness",
    url: "https://mamaglow.com",
    emoji: "🤰🏾",
    color: "from-rose-400 to-fuchsia-500",
    products: ["Doula Services", "Birth Education", "Postpartum Tea"],
    isNewBrand: false,
    founder: "Latham Thomas"
  },
  {
    name: "Sacred Mama Organics",
    tagline: "Womb-to-postpartum wellness",
    description: "Plant-based body care + herbal teas for fertility, pregnancy, postpartum, and womb healing.",
    category: "Womb Wellness",
    url: "https://sacredmamaorganics.com",
    emoji: "🌸",
    color: "from-rose-300 to-pink-500",
    products: ["Postpartum Bath", "Womb Tea", "Body Butter"],
    isNewBrand: false,
    founder: "Stephanie Adimora"
  }
];

// Get featured business — rotates DAILY now (was every 2 weeks).
// Uses day-of-year + a stable per-user offset so two users on the same day
// can see different brands (more visibility for the businesses, less stale
// for users), but each user sees a stable pick for the whole day.
const getFeaturedBusiness = (userIdSeed = "") => {
  const now = new Date();
  // Day-of-year (1-366), independent of timezone for our purposes
  const startOfYear = Date.UTC(now.getUTCFullYear(), 0, 0);
  const dayOfYear = Math.floor(
    (Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()) - startOfYear) / 86400000
  );
  // Per-user offset (small int) so different users see different brands today
  let userOffset = 0;
  for (let i = 0; i < userIdSeed.length; i++) userOffset = (userOffset + userIdSeed.charCodeAt(i)) % 997;
  const index = (dayOfYear + userOffset) % FEATURED_BUSINESSES.length;
  return FEATURED_BUSINESSES[index];
};

const Dashboard = () => {
  const { user, logout, getAuthHeader, token } = useAuth();
  const { isDark, toggleTheme } = useTheme();
  const [dailyAffirmation, setDailyAffirmation] = useState(null);
  const [desktopMenuOpen, setDesktopMenuOpen] = useState(false);
  const [accountMenuOpen, setAccountMenuOpen] = useState(false);
  const [journalEntries, setJournalEntries] = useState([]);
  const [weeklyChallenges, setWeeklyChallenges] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [weeklyFeatured, setWeeklyFeatured] = useState(null);
  const [weeklySummary, setWeeklySummary] = useState(null);
  const [upgradeModal, setUpgradeModal] = useState({ isOpen: false, feature: "", tier: "awakening" });
  const [soulConnectionQuiz, setSoulConnectionQuiz] = useState(null);
  const [showSoulQuizModal, setShowSoulQuizModal] = useState(false);
  const [showVibeOutHub, setShowVibeOutHub] = useState(false);
  const [showWeeklyReport, setShowWeeklyReport] = useState(false);
  const [showMoonCycle, setShowMoonCycle] = useState(false);
  const [showMiracleDeck, setShowMiracleDeck] = useState(false);
  const [showHealingTogether, setShowHealingTogether] = useState(false);
  const [showManifestationCircles, setShowManifestationCircles] = useState(false);
  const [showNotificationSettings, setShowNotificationSettings] = useState(false);
  const [unreadLoveMessages, setUnreadLoveMessages] = useState(0);
  const [nutritionProfile, setNutritionProfile] = useState(null);
  const desktopMenuRef = useRef(null);
  const accountMenuRef = useRef(null);
  const [searchParams, setSearchParams] = useSearchParams();

  // Deep-link handler: when a notification (or any inbound link) lands on
  // /dashboard?open=<modalKey>, auto-open that modal so the user actually
  // sees what they were promised — instead of being dumped on the dashboard
  // and hunting for it. Strips the param so refresh doesn't re-trigger.
  useEffect(() => {
    const open = searchParams.get("open");
    if (!open) return;
    const triggers = {
      "miracle-deck": () => setShowMiracleDeck(true),
      "manifestation": () => setShowMiracleDeck(true),       // alias
      "miracle-card": () => setShowMiracleDeck(true),        // alias
      "moon-cycle": () => setShowMoonCycle(true),
      "vibe-out": () => setShowVibeOutHub(true),
      "weekly-report": () => setShowWeeklyReport(true),
      "healing-together": () => setShowHealingTogether(true),
      "manifestation-circles": () => setShowManifestationCircles(true),
      "soul-quiz": () => setShowSoulQuizModal(true),
    };
    const fn = triggers[open];
    if (fn) {
      fn();
      // Drop the query param so refresh / back-nav doesn't keep re-opening it.
      const next = new URLSearchParams(searchParams);
      next.delete("open");
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);
  
  // Body type info for display
  const BODY_TYPE_INFO = {
    ectomorph: { name: "Ectomorph", emoji: "🦋", traits: ["Fast metabolism", "Lean build", "Long limbs"], focus: "Building muscle & eating enough" },
    mesomorph: { name: "Mesomorph", emoji: "💪🏾", traits: ["Athletic build", "Medium frame", "Naturally muscular"], focus: "Balance & avoiding overtraining" },
    endomorph: { name: "Endomorph", emoji: "🌺", traits: ["Curvy build", "Wider hips", "Stores easily"], focus: "Managing carbs & staying active" }
  };
  
  // Dosha info for Ayurvedic display
  const DOSHA_INFO = {
    vata: { name: "Vata", emoji: "🌬️", element: "Air & Space", focus: "Grounding & warm foods", color: "from-sky-500 to-blue-500" },
    pitta: { name: "Pitta", emoji: "🔥", element: "Fire & Water", focus: "Cooling foods & balance", color: "from-orange-500 to-red-500" },
    kapha: { name: "Kapha", emoji: "🌍", element: "Earth & Water", focus: "Light foods & movement", color: "from-green-500 to-emerald-500" }
  };
  
  // Journey progress for unlock prompts
  const { progress: journeyProgress, activePrompt, dismissPrompt } = useJourneyProgress();

  const navigate = useNavigate();

  // Auto-route brand-new users (never completed, never skipped the quiz) to the onboarding quiz.
  // This fixes the race condition where Register.js tries to navigate to /quiz but the auth
  // redirect sends users to /dashboard first. Centralizing the check here means any entry path
  // (register, login, social auth, etc.) correctly onboards first.
  useEffect(() => {
    if (user && user.quiz_completed === false && user.quiz_skipped === false) {
      navigate("/quiz", { replace: true });
    }
  }, [user, navigate]);

  // Fetch Soul Connection Quiz result from database (persists across sessions)
  const fetchSoulConnectionQuiz = async () => {
    try {
      // First check localStorage for immediate display
      const localResult = localStorage.getItem('soul_connection_quiz_result');
      if (localResult) {
        setSoulConnectionQuiz(JSON.parse(localResult));
      }
      
      // Then fetch from database to ensure persistence
      const response = await axios.get(`${API}/soul-connection-quiz`, {
        headers: getAuthHeader()
      });
      
      if (response.data.has_result && response.data.quiz_result) {
        const dbResult = response.data.quiz_result;
        setSoulConnectionQuiz(dbResult);
        // Sync localStorage with database
        localStorage.setItem('soul_connection_quiz_result', JSON.stringify(dbResult));
      }
    } catch (error) {
      console.error("Failed to fetch soul connection quiz:", error);
    }
  };

  const fetchWeeklySummary = async () => {
    try {
      // Send the browser's timezone offset (minutes) so the backend can
      // anchor "today" / "this week" to the user's local clock instead of UTC.
      // getTimezoneOffset is positive for zones behind UTC (e.g. PST = 480).
      const tzOffset = new Date().getTimezoneOffset();
      const response = await axios.get(`${API}/weekly-summary`, {
        headers: getAuthHeader(),
        params: { tz_offset: tzOffset },
      });
      setWeeklySummary(response.data);
    } catch (error) {
      console.error("Failed to fetch weekly summary:", error);
    }
  };

  const fetchNutritionProfile = async () => {
    try {
      const response = await axios.get(`${API}/nutrition/wellness-quiz`, {
        headers: getAuthHeader()
      });
      if (response.data.quiz_completed && response.data.nutrition_goals) {
        setNutritionProfile(response.data.nutrition_goals);
      }
    } catch (error) {
      console.error("Failed to fetch nutrition profile:", error);
    }
  };

  const handlePremiumClick = (e, feature, tier = "awakening") => {
    if (!user?.is_premium) {
      e.preventDefault();
      setUpgradeModal({ isOpen: true, feature, tier });
    }
  };

  const checkAdminAccess = async () => {
    try {
      const response = await axios.get(`${API}/admin/check`, {
        headers: getAuthHeader()
      });
      setIsAdmin(response.data.is_admin);
    } catch (error) {
      setIsAdmin(false);
    }
  };

  const fetchWeeklyFeatured = async () => {
    try {
      const response = await axios.get(`${API}/weekly/featured`, {
        headers: getAuthHeader()
      });
      setWeeklyFeatured(response.data);
    } catch (error) {
      console.error("Failed to fetch weekly featured:", error);
    }
  };

  const fetchDailyAffirmation = async () => {
    try {
      const response = await axios.get(`${API}/affirmations/daily`, {
        headers: getAuthHeader()
      });
      setDailyAffirmation(response.data);
    } catch (error) {
      console.error("Failed to fetch affirmation:", error);
    }
  };

  const fetchJournalEntries = async () => {
    try {
      const response = await axios.get(`${API}/journal`, {
        headers: getAuthHeader()
      });
      setJournalEntries(response.data);
    } catch (error) {
      console.error("Failed to fetch journal entries:", error);
    }
  };

  const fetchWeeklyChallenges = async () => {
    try {
      const response = await axios.get(`${API}/weekly-challenges/summary`, {
        headers: getAuthHeader()
      });
      setWeeklyChallenges(response.data);
    } catch (error) {
      console.error("Failed to fetch weekly challenges:", error);
    }
  };

  // Fetch unread love messages count for badge
  const fetchUnreadLoveMessages = async () => {
    try {
      const response = await axios.get(`${API}/healing-together/unread-count`, {
        headers: getAuthHeader()
      });
      setUnreadLoveMessages(response.data.unread_count || 0);
    } catch (error) {
      // Silently fail - not critical
      console.error("Failed to fetch unread love messages:", error);
    }
  };

  useEffect(() => {
    // Scroll to top when dashboard loads
    window.scrollTo({ top: 0, behavior: 'instant' });
    fetchDailyAffirmation();
    checkAdminAccess();
    fetchWeeklyFeatured();
    fetchWeeklySummary();
    fetchSoulConnectionQuiz();
    fetchNutritionProfile();
    fetchUnreadLoveMessages();
    if (user?.is_premium) {
      fetchJournalEntries();
      fetchWeeklyChallenges();
      // Initialize push notifications
      initializePushNotifications(token).catch(console.error);
    }
  }, [user]);

  // ─── Live-refresh hooks (Feb 2026) ─────────────────────────────────
  // The dashboard is the single source of truth for "today's state".
  // To keep it honest:
  //   1. Re-fetch when the tab becomes visible again (user logged a meal
  //      on /nutrition then returned — without this, today_rituals stays stale).
  //   2. Re-fetch when the local date crosses midnight (new day = empty grid).
  useEffect(() => {
    if (!user) return;
    const refresh = () => {
      fetchWeeklySummary();
      fetchDailyAffirmation();
      fetchNutritionProfile();
      if (user?.is_premium) {
        fetchJournalEntries();
      }
    };

    const onVisibility = () => {
      if (document.visibilityState === "visible") refresh();
    };
    const onFocus = () => refresh();

    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("focus", onFocus);

    // Cross-midnight detector: snapshot today's local date string, check
    // every 60 sec if it has rolled over.
    const initialDate = new Date().toLocaleDateString();
    const dayChangeInterval = setInterval(() => {
      const nowDate = new Date().toLocaleDateString();
      if (nowDate !== initialDate) {
        // It's a new day — full refresh and reload to clear any in-memory
        // state that's keyed off "today".
        refresh();
      }
    }, 60 * 1000);

    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("focus", onFocus);
      clearInterval(dayChangeInterval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Close menus when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (desktopMenuRef.current && !desktopMenuRef.current.contains(event.target)) {
        setDesktopMenuOpen(false);
      }
      if (accountMenuRef.current && !accountMenuRef.current.contains(event.target)) {
        setAccountMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // "Save Your Streak" 7 PM in-app nudge — gentle toast when rituals are incomplete
  // Shows at/after 7 PM local time, once per day, if streak is alive and <3 rituals done
  useEffect(() => {
    if (!weeklySummary?.today_rituals) return;
    const streak = weeklySummary.current_streak || 0;
    if (streak < 1) return;
    const rituals = weeklySummary.today_rituals;
    const completedCount = Object.values(rituals).filter(Boolean).length;
    if (completedCount >= 3) return; // Enough activity; streak is safe

    const now = new Date();
    if (now.getHours() < 19) return; // Before 7 PM local

    const todayKey = `lala_streak_nudge_shown_${now.toISOString().slice(0, 10)}`;
    if (typeof window !== "undefined" && window.localStorage.getItem(todayKey)) return;

    const t = setTimeout(() => {
      toast(`🔥 Keep your ${streak}-day streak alive`, {
        description: "Just one quick ritual — a vibe check, an affirmation, or a line in your journal — before midnight.",
        duration: 9000,
        action: {
          label: "Vibe Check",
          onClick: () => { window.location.href = "/vibe-check"; },
        },
      });
      try { window.localStorage.setItem(todayKey, "1"); } catch {}
    }, 1500);
    return () => clearTimeout(t);
  }, [weeklySummary]);

  const navigation = [
    // 🌅 Morning Ritual — how you greet the day and close the day
    { name: "Morning Routine", href: "/morning", icon: Sunrise, color: "bg-amber-500/20", category: "morning" },
    { name: "Daily Vibe Check", href: "/vibe-check", icon: Smile, color: "bg-gradient-to-r from-pink-500/20 to-orange-500/20", category: "morning", badge: "New" },
    { name: "Affirmations", href: "/affirmations", icon: Sparkles, color: "bg-accent/20", category: "morning" },
    { name: "Journal", href: "/journal", icon: BookOpen, color: "bg-primary/10", premium: true, category: "morning" },
    { name: "Dream Journal", href: "/dreams", icon: CloudMoon, color: "bg-indigo-500/20", category: "morning", badge: "New" },
    { name: "Bedtime Routine", href: "/bedtime", icon: Bed, color: "bg-indigo-500/20", category: "morning" },

    // ✨ Spiritual — soul work, inner practice, meaning
    { name: "My Wellness Journey", href: "/wellness-journey", icon: TrendingUp, color: "bg-violet-500/20", category: "spiritual" },
    { name: "My Affirmations", href: "/custom-affirmations", icon: Mic, color: "bg-accent/10", premium: true, category: "spiritual" },
    { name: "Angel Numbers", href: "/angel-numbers", icon: Star, color: "bg-accent/20", category: "spiritual" },
    { name: "Spirit Animals", href: "/spirit-animals", icon: PawPrint, color: "bg-emerald-500/20", category: "spiritual" },
    { name: "Awakening", href: "/spiritual-awakening", icon: BookText, color: "bg-purple-500/20", category: "spiritual" },
    { name: "Meditations", href: "/guided-meditations", icon: Headphones, color: "bg-cyan-500/20", category: "spiritual" },
    { name: "Sound Sanctuary", href: "/music", icon: Music, color: "bg-primary/20", premium: true, category: "spiritual" },
    { name: "Grounding Hour", href: "/grounding", icon: TreeDeciduous, color: "bg-gradient-to-r from-green-500/20 to-emerald-500/20", category: "spiritual", badge: "New" },
    { name: "Courageous Conversations", href: "/courage-deck", icon: MessageSquare, color: "bg-gradient-to-r from-rose-500/20 to-orange-500/20", premium: true, category: "spiritual", badge: "New" },

    // 🌱 Body — nourish the physical + mental vessel
    { name: "Nutrition & Exercise", href: "/nutrition", icon: Salad, color: "bg-green-500/20", category: "body" },
    { name: "Symptom Journal", href: "/symptom-journal", icon: Stethoscope, color: "bg-gradient-to-r from-emerald-500/20 to-teal-500/20", category: "body", badge: "New" },
    { name: "Readables", href: "/readables", icon: BookOpen, color: "bg-violet-500/20", category: "body" },
    { name: "Mindful Hobbies", href: "/mindful-hobbies", icon: Palette, color: "bg-rose-500/20", category: "body" },
    { name: "Baby Go to Therapy", href: "/therapy", icon: HandHeart, color: "bg-rose-500/20", category: "body" },

    // 💞 Community — show up for each other
    { name: "Soul Circle", href: "/soul-circle", icon: Users, color: "bg-gradient-to-r from-violet-500/20 to-pink-500/20", category: "community", badge: "New" },
    { name: "Rooted Resources", href: "/rooted-resources", icon: Leaf, color: "bg-emerald-500/20", category: "community" },
    { name: "Leaderboard", href: "/leaderboard", icon: Trophy, color: "bg-amber-500/20", premium: true, category: "community" },
    { name: "Sacred Collection", href: "/favorites", icon: Heart, color: "bg-pink-500/20", category: "community" },
    { name: "Reminders", href: "/reminder-settings", icon: Bell, color: "bg-amber-500/20", category: "community" },
    { name: "Share App", href: "/share", icon: QrCode, color: "bg-cyan-500/20", category: "community" },
    { name: "Our Story", href: "/about", icon: Heart, color: "bg-pink-500/20", category: "community" },

    ...(isAdmin ? [{ name: "Admin Dashboard", href: "/admin", icon: BarChart3, color: "bg-red-500/20", isAdmin: true, category: "admin" }] : []),
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* 3-second breathing orb shown once per day — nervous-system primer */}
      <BreatheFirst />
      {/* Floating pull-up drawer with every feature (kept off the main scroll for altar feel) */}
      <ExploreDrawer
        isPremium={user?.is_premium}
        ritualsCompleted={weeklySummary?.today_rituals ? Object.values(weeklySummary.today_rituals).filter(Boolean).length : 0}
        ritualsTotal={weeklySummary?.today_rituals ? Object.keys(weeklySummary.today_rituals).length : 0}
        streakDays={weeklySummary?.current_streak ?? 0}
      />
      {/* Header */}
      <header className="sticky top-0 z-50 bg-background/80 backdrop-blur-md border-b border-border/50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center">
            {/* Light Mode: Leaf Icon with Text */}
            {!isDark && (
              <div className="flex items-center gap-2 group">
                <Leaf 
                  className="h-8 w-8 text-emerald-700 transition-all duration-300 group-hover:scale-110 flex-shrink-0"
                />
                <span className="text-lg sm:text-xl font-semibold text-emerald-800 tracking-wide whitespace-nowrap">
                  Lala Land
                </span>
              </div>
            )}
            {/* Dark Mode: Glowing Leaf with Text */}
            {isDark && (
              <div className="flex items-center gap-2 group">
                <div className="relative flex-shrink-0">
                  <Leaf 
                    className="h-9 w-9 sm:h-10 sm:w-10 text-emerald-400 transition-all duration-500 group-hover:scale-110"
                    style={{
                      filter: 'drop-shadow(0 0 8px rgba(52, 211, 153, 0.8)) drop-shadow(0 0 16px rgba(52, 211, 153, 0.6)) drop-shadow(0 0 24px rgba(52, 211, 153, 0.4))'
                    }}
                  />
                  <div className="absolute inset-0 bg-emerald-400/40 blur-xl rounded-full animate-pulse"></div>
                </div>
                <span 
                  className="text-lg sm:text-xl font-semibold text-white tracking-wide whitespace-nowrap"
                  style={{
                    textShadow: '0 0 10px rgba(52, 211, 153, 0.6), 0 0 20px rgba(52, 211, 153, 0.4)'
                  }}
                >
                  Lala Land
                </span>
              </div>
            )}
          </Link>

          {/* Explore Nav - Dropdown Menu (visible on all sizes) */}
          <div className="block relative" ref={desktopMenuRef}>
            <Button
              variant="outline"
              onClick={() => setDesktopMenuOpen(!desktopMenuOpen)}
              className="group flex items-center gap-1.5 px-3 sm:px-4 py-2 rounded-full border-border/50 hover:bg-secondary/20 hover:border-violet-500/40 transition-colors"
              data-testid="desktop-nav-menu-btn"
            >
              <Compass
                className={`h-4 w-4 text-violet-500 transition-transform duration-500 ease-out ${
                  desktopMenuOpen ? 'rotate-[135deg]' : 'group-hover:rotate-45'
                }`}
              />
              <span className="text-sm font-medium hidden sm:inline">Explore</span>
              <ChevronDown className={`h-4 w-4 transition-transform ${desktopMenuOpen ? 'rotate-180' : ''}`} />
            </Button>
            
            {/* Dropdown Content - Organized by category: Morning / Spiritual / Body / Community */}
            {desktopMenuOpen && (
              <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 w-[min(18rem,calc(100vw-1.5rem))] sm:w-72 bg-background border border-border rounded-xl shadow-lg z-50 py-2 max-h-[70vh] overflow-y-auto">
                {/* Morning Ritual */}
                <div className="px-3 py-1">
                  <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">🌅 Morning Ritual</p>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-0 px-1 pb-2">
                  {navigation.filter(item => item.category === 'morning').map((item) => (
                    <Link
                      key={item.name}
                      to={item.href}
                      onClick={() => setDesktopMenuOpen(false)}
                      className={`flex items-center gap-1 px-2 py-1.5 rounded-md transition-colors text-xs text-muted-foreground hover:text-primary hover:bg-secondary/30`}
                    >
                      <item.icon className="h-3 w-3 flex-shrink-0" />
                      <span className="truncate">{item.name}</span>
                      {item.premium && !user?.is_premium && <Crown className="h-2 w-2 text-accent ml-auto flex-shrink-0" />}
                    </Link>
                  ))}
                </div>

                {/* Spiritual */}
                <div className="border-t border-border/50 mt-1 pt-2 px-3 py-1">
                  <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">✨ Spiritual</p>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-0 px-1 pb-2">
                  {navigation.filter(item => item.category === 'spiritual').map((item) => (
                    <Link
                      key={item.name}
                      to={item.href}
                      onClick={() => setDesktopMenuOpen(false)}
                      className={`flex items-center gap-1 px-2 py-1.5 rounded-md transition-colors text-xs text-muted-foreground hover:text-primary hover:bg-secondary/30`}
                    >
                      <item.icon className="h-3 w-3 flex-shrink-0" />
                      <span className="truncate">{item.name}</span>
                      {item.premium && !user?.is_premium && <Crown className="h-2 w-2 text-accent ml-auto flex-shrink-0" />}
                    </Link>
                  ))}
                </div>

                {/* Body */}
                <div className="border-t border-border/50 mt-1 pt-2 px-3 py-1">
                  <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">🌱 Body</p>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-0 px-1 pb-2">
                  {navigation.filter(item => item.category === 'body').map((item) => (
                    <Link
                      key={item.name}
                      to={item.href}
                      onClick={() => setDesktopMenuOpen(false)}
                      className={`flex items-center gap-1 px-2 py-1.5 rounded-md transition-colors text-xs text-muted-foreground hover:text-primary hover:bg-secondary/30`}
                    >
                      <item.icon className="h-3 w-3 flex-shrink-0" />
                      <span className="truncate">{item.name}</span>
                      {item.premium && !user?.is_premium && <Crown className="h-2 w-2 text-accent ml-auto flex-shrink-0" />}
                    </Link>
                  ))}
                </div>

                {/* Community */}
                <div className="border-t border-border/50 mt-1 pt-2 px-3 py-1">
                  <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">💞 Community</p>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-0 px-1 pb-2">
                  {navigation.filter(item => item.category === 'community').map((item) => (
                    <Link
                      key={item.name}
                      to={item.href}
                      onClick={() => setDesktopMenuOpen(false)}
                      className={`flex items-center gap-1 px-2 py-1.5 rounded-md transition-colors text-xs text-muted-foreground hover:text-primary hover:bg-secondary/30`}
                    >
                      <item.icon className="h-3 w-3 flex-shrink-0" />
                      <span className="truncate">{item.name}</span>
                      {item.badge && (
                        <span className="ml-1 px-1.5 py-0.5 text-[9px] font-bold bg-gradient-to-r from-rose-500 to-orange-500 text-white rounded-full flex-shrink-0">
                          {item.badge}
                        </span>
                      )}
                      {item.premium && !user?.is_premium && <Crown className="h-2 w-2 text-accent ml-auto flex-shrink-0" />}
                    </Link>
                  ))}
                </div>

                {/* Admin */}
                {isAdmin && (
                  <>
                    <div className="border-t border-red-500/30 mt-1 pt-2 px-3 py-1">
                      <p className="text-[10px] font-semibold text-red-500 uppercase tracking-wider">Admin</p>
                    </div>
                    <div className="px-1 pb-2">
                      {navigation.filter(item => item.category === 'admin').map((item) => (
                        <Link
                          key={item.name}
                          to={item.href}
                          onClick={() => setDesktopMenuOpen(false)}
                          className="flex items-center gap-1 px-2 py-1.5 rounded-md transition-colors text-xs bg-gradient-to-r from-red-500/20 to-orange-500/20 text-red-500 hover:from-red-500/30 hover:to-orange-500/30 font-semibold border border-red-500/30"
                        >
                          <item.icon className="h-3 w-3 flex-shrink-0" />
                          <span className="truncate">{item.name}</span>
                        </Link>
                      ))}
                    </div>
                  </>
                )}
              </div>
            )}
          </div>

          <div className="flex items-center gap-3">
            {/* Dark Mode Toggle */}
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleTheme}
              className="h-9 w-9 rounded-full"
              data-testid="theme-toggle"
            >
              {isDark ? (
                <Sun className="h-4 w-4 text-accent" />
              ) : (
                <Moon className="h-4 w-4 text-muted-foreground" />
              )}
            </Button>

            {/* Notification Center */}
            <NotificationCenter 
              onOpenSettings={() => setShowNotificationSettings(true)} 
            />

            {!user?.is_premium && (
              <Link to="/subscription">
                <Button 
                  size="sm" 
                  className="bg-accent text-accent-foreground rounded-full hover:bg-accent/90 hidden sm:flex"
                  data-testid="header-upgrade-btn"
                >
                  <Crown className="h-4 w-4 mr-2" />
                  Upgrade
                </Button>
              </Link>
            )}
            {user?.is_premium && (
              <Link to="/subscription/manage">
                <span className="text-xs bg-accent/20 text-accent px-3 py-1 rounded-full font-medium hidden sm:block cursor-pointer hover:bg-accent/30 transition-colors">
                  Premium
                </span>
              </Link>
            )}

            {/* Account Menu — avatar opens dropdown with Wellness/Voice + Profile + Sign out */}
            <div className="relative" ref={accountMenuRef}>
              <button
                onClick={() => setAccountMenuOpen((v) => !v)}
                aria-label="Account menu"
                data-testid="header-account-menu-btn"
                className="ml-1 hover:opacity-80 transition-opacity rounded-full"
              >
                <UserAvatar
                  src={user?.avatar_url}
                  name={user?.name || user?.email || "?"}
                  size={32}
                />
              </button>

              {accountMenuOpen && (
                <div
                  className="absolute right-0 mt-2 w-60 bg-background border border-border rounded-xl shadow-lg z-50 py-2"
                  role="menu"
                  data-testid="account-menu-dropdown"
                >
                  <div className="px-3 py-2 border-b border-border/40">
                    <p className="text-sm font-semibold text-primary truncate">
                      {user?.name || "Soul Seeker"}
                    </p>
                    <p className="text-[11px] text-muted-foreground truncate">
                      {user?.email}
                    </p>
                  </div>

                  <Link
                    to="/profile"
                    onClick={() => setAccountMenuOpen(false)}
                    className="flex items-center gap-2 px-3 py-2 text-sm text-foreground hover:bg-secondary/40 transition-colors"
                    data-testid="account-menu-profile-link"
                  >
                    <UserAvatar
                      src={user?.avatar_url}
                      name={user?.name || "?"}
                      size={20}
                    />
                    Profile & Settings
                  </Link>

                  <div className="border-t border-border/40 my-1" />

                  <button
                    onClick={() => {
                      setAccountMenuOpen(false);
                      logout();
                    }}
                    className="flex items-center gap-2 w-full px-3 py-2 text-sm text-red-500 hover:bg-red-500/10 transition-colors text-left"
                    data-testid="account-menu-logout-btn"
                  >
                    <LogOut className="h-4 w-4" />
                    Sign out
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 py-12 [&>*]:!mb-10 md:[&>*]:!mb-14">
        {/* Modern Welcome Section */}
        <div className="mb-10 animate-fade-in">
          <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-primary/5 via-accent/5 to-purple-500/5 border border-border/30 p-8">
            {/* Decorative Elements */}
            <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-bl from-accent/10 to-transparent rounded-full blur-3xl -translate-y-1/2 translate-x-1/2"></div>
            <div className="absolute bottom-0 left-0 w-48 h-48 bg-gradient-to-tr from-purple-500/10 to-transparent rounded-full blur-2xl translate-y-1/2 -translate-x-1/4"></div>
            
            <div className="relative z-10 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
              {/* Left: Welcome Text — header is now cleanly broken up into 3 clear levels:
                  1. Tiny uppercase "welcome back" eyebrow  (intentionally subtle)
                  2. Big name headline (the real focal point)
                  3. Forever Rooted crown + supporting copy  (pushed below the name with breathing room)  */}
              <div className="flex-1 text-center sm:text-left">
                <p className="text-[10px] sm:text-xs uppercase tracking-[0.3em] text-accent/80 font-medium mb-3">
                  Welcome back
                </p>
                <h1 className="text-3xl sm:text-4xl md:text-5xl font-light text-primary mb-3 leading-tight" data-testid="welcome-heading">
                  {user?.name || "Beautiful Soul"}
                </h1>
                {user?.subscription_tier === 'forever_rooted' && (
                  <div className="flex justify-center sm:justify-start mb-3">
                    <span
                      data-testid="forever-rooted-badge"
                      className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-semibold text-amber-700 dark:text-amber-300 bg-amber-500/10 border border-amber-500/30 px-2.5 py-1 rounded-full"
                      title="Forever Rooted — Lifetime Member"
                    >
                      <Crown className="h-3 w-3 fill-amber-500 text-amber-500" />
                      Forever Rooted
                    </span>
                  </div>
                )}
                <p className="text-muted-foreground text-sm md:text-base">
                  Your spiritual journey continues. What calls to you today?
                </p>
              </div>

              {/* Right: Quick Actions - Grouped by Morning / Spiritual / Body / Community */}
              <div className="flex flex-col gap-3 w-full sm:max-w-3xl mx-auto">
                {/* 🌅 Morning Ritual */}
                <div className="text-center sm:text-left">
                  <p className="text-[10px] font-semibold text-muted-foreground/80 uppercase tracking-wider mb-1.5 hidden sm:block">🌅 Morning Ritual</p>
                  <div className="flex flex-wrap items-center justify-center sm:justify-start gap-2 sm:gap-2.5">
                    <Link to="/vibe-check">
                      <Button variant="outline" size="sm" className="rounded-full border-pink-500/30 text-pink-600 hover:bg-pink-500/10 hover:text-pink-700 h-9 w-9 sm:h-auto sm:w-auto p-2 sm:px-3" data-testid="quick-vibe-check-btn">
                        <Smile className="h-4 w-4" />
                        <span className="hidden sm:inline ml-2">Vibe</span>
                      </Button>
                    </Link>
                    <Link to="/affirmations">
                      <Button variant="outline" size="sm" className="rounded-full border-yellow-500/30 text-yellow-600 hover:bg-yellow-500/10 hover:text-yellow-700 h-9 w-9 sm:h-auto sm:w-auto p-2 sm:px-3" data-testid="quick-affirmations-btn">
                        <Sparkles className="h-4 w-4" />
                        <span className="hidden sm:inline ml-2">Affirm</span>
                      </Button>
                    </Link>
                    <Link to="/guided-meditations">
                      <Button variant="outline" size="sm" className="rounded-full border-cyan-500/30 text-cyan-600 hover:bg-cyan-500/10 hover:text-cyan-700 h-9 w-9 sm:h-auto sm:w-auto p-2 sm:px-3" data-testid="quick-meditations-btn">
                        <Wind className="h-4 w-4" />
                        <span className="hidden sm:inline ml-2">Meditate</span>
                      </Button>
                    </Link>
                    <Link to="/grounding">
                      <Button variant="outline" size="sm" className="rounded-full border-green-500/30 text-green-600 hover:bg-green-500/10 hover:text-green-700 h-9 w-9 sm:h-auto sm:w-auto p-2 sm:px-3" data-testid="quick-grounding-btn">
                        <TreeDeciduous className="h-4 w-4" />
                        <span className="hidden sm:inline ml-2">Ground</span>
                      </Button>
                    </Link>
                    <Link to="/dreams">
                      <Button variant="outline" size="sm" className="rounded-full border-indigo-500/30 text-indigo-600 hover:bg-indigo-500/10 hover:text-indigo-700 h-9 w-9 sm:h-auto sm:w-auto p-2 sm:px-3" data-testid="quick-dreams-btn">
                        <CloudMoon className="h-4 w-4" />
                        <span className="hidden sm:inline ml-2">Dreams</span>
                      </Button>
                    </Link>
                  </div>
                </div>

                {/* ✨ Spiritual */}
                <div className="text-center sm:text-left">
                  <p className="text-[10px] font-semibold text-muted-foreground/80 uppercase tracking-wider mb-1.5 hidden sm:block">✨ Spiritual</p>
                  <div className="flex flex-wrap items-center justify-center sm:justify-start gap-2 sm:gap-2.5">
                    <Link to="/wellness-journey">
                      <Button variant="outline" size="sm" className="rounded-full border-violet-500/30 text-violet-600 hover:bg-violet-500/10 hover:text-violet-700 h-9 w-9 sm:h-auto sm:w-auto p-2 sm:px-3" data-testid="quick-wellness-journey-btn">
                        <TrendingUp className="h-4 w-4" />
                        <span className="hidden sm:inline ml-2">Journey</span>
                      </Button>
                    </Link>
                    <Link to="/spiritual-awakening">
                      <Button variant="outline" size="sm" className="rounded-full border-indigo-500/30 text-indigo-600 hover:bg-indigo-500/10 hover:text-indigo-700 h-9 w-9 sm:h-auto sm:w-auto p-2 sm:px-3" data-testid="quick-spiritual-btn">
                        <Sparkles className="h-4 w-4" />
                        <span className="hidden sm:inline ml-2">Awaken</span>
                      </Button>
                    </Link>
                    <Link to="/angel-numbers">
                      <Button variant="outline" size="sm" className="rounded-full border-purple-500/30 text-purple-600 hover:bg-purple-500/10 hover:text-purple-700 h-9 w-9 sm:h-auto sm:w-auto p-2 sm:px-3" data-testid="quick-angel-numbers-btn">
                        <Hash className="h-4 w-4" />
                        <span className="hidden sm:inline ml-2">Angels</span>
                      </Button>
                    </Link>
                    <Button variant="outline" size="sm" className="rounded-full border-amber-500/30 text-amber-600 hover:bg-amber-500/10 hover:text-amber-700 h-9 w-9 sm:h-auto sm:w-auto p-2 sm:px-3" onClick={() => setShowMoonCycle(true)} data-testid="quick-moon-btn">
                      <Moon className="h-4 w-4" />
                      <span className="hidden sm:inline ml-2">Moon</span>
                    </Button>
                    <Button variant="outline" size="sm" className="rounded-full border-violet-500/30 text-violet-600 hover:bg-violet-500/10 hover:text-violet-700 h-9 w-9 sm:h-auto sm:w-auto p-2 sm:px-3" onClick={() => setShowMiracleDeck(true)} data-testid="quick-miracle-deck-btn">
                      <Star className="h-4 w-4" />
                      <span className="hidden sm:inline ml-2">Cards</span>
                    </Button>
                    <Link to="/music">
                      <Button variant="outline" size="sm" className="rounded-full border-rose-500/30 text-rose-600 hover:bg-rose-500/10 hover:text-rose-700 h-9 w-9 sm:h-auto sm:w-auto p-2 sm:px-3" data-testid="quick-music-btn">
                        <Music className="h-4 w-4" />
                        <span className="hidden sm:inline ml-2">Vibes</span>
                      </Button>
                    </Link>
                  </div>
                </div>

                {/* 🌱 Body */}
                <div className="text-center sm:text-left">
                  <p className="text-[10px] font-semibold text-muted-foreground/80 uppercase tracking-wider mb-1.5 hidden sm:block">🌱 Body</p>
                  <div className="flex flex-wrap items-center justify-center sm:justify-start gap-2 sm:gap-2.5">
                    <Link to="/nutrition">
                      <Button variant="outline" size="sm" className="rounded-full border-emerald-500/30 text-emerald-600 hover:bg-emerald-500/10 hover:text-emerald-700 h-9 w-9 sm:h-auto sm:w-auto p-2 sm:px-3" data-testid="quick-nutrition-btn">
                        <Apple className="h-4 w-4" />
                        <span className="hidden sm:inline ml-2">Nutrition</span>
                      </Button>
                    </Link>
                    <Link to="/readables">
                      <Button variant="outline" size="sm" className="rounded-full border-amber-500/30 text-amber-600 hover:bg-amber-500/10 hover:text-amber-700 h-9 w-9 sm:h-auto sm:w-auto p-2 sm:px-3" data-testid="quick-readables-btn">
                        <BookOpen className="h-4 w-4" />
                        <span className="hidden sm:inline ml-2">Read</span>
                      </Button>
                    </Link>
                    <Link to="/therapy">
                      <Button variant="outline" size="sm" className="rounded-full border-teal-500/30 text-teal-600 hover:bg-teal-500/10 hover:text-teal-700 h-9 w-9 sm:h-auto sm:w-auto p-2 sm:px-3" data-testid="quick-therapy-btn">
                        <HandHeart className="h-4 w-4" />
                        <span className="hidden sm:inline ml-2">Therapy</span>
                      </Button>
                    </Link>
                    <Button variant="outline" size="sm" className="rounded-full border-purple-500/30 text-purple-600 hover:bg-purple-500/10 hover:text-purple-700 h-9 w-9 sm:h-auto sm:w-auto p-2 sm:px-3" onClick={() => setShowWeeklyReport(true)} data-testid="quick-weekly-report-btn">
                      <Calendar className="h-4 w-4" />
                      <span className="hidden sm:inline ml-2">Report</span>
                    </Button>
                  </div>
                </div>

                {/* 💞 Community */}
                <div className="text-center sm:text-left">
                  <p className="text-[10px] font-semibold text-muted-foreground/80 uppercase tracking-wider mb-1.5 hidden sm:block">💞 Community</p>
                  <div className="flex flex-wrap items-center justify-center sm:justify-start gap-2 sm:gap-2.5">
                    <Link to="/soul-circle">
                      <Button variant="outline" size="sm" className="rounded-full border-sky-500/30 text-sky-600 hover:bg-sky-500/10 hover:text-sky-700 h-9 w-9 sm:h-auto sm:w-auto p-2 sm:px-3" data-testid="quick-soul-circle-btn">
                        <MessagesSquare className="h-4 w-4" />
                        <span className="hidden sm:inline ml-2">Circle</span>
                      </Button>
                    </Link>
                    <Button variant="outline" size="sm" className="rounded-full border-rose-500/30 text-rose-600 hover:bg-rose-500/10 hover:text-rose-700 h-9 w-9 sm:h-auto sm:w-auto p-2 sm:px-3 relative" onClick={() => { setShowHealingTogether(true); setUnreadLoveMessages(0); }} data-testid="quick-healing-together-btn">
                      <Heart className="h-4 w-4" />
                      <span className="hidden sm:inline ml-2">Us</span>
                      {unreadLoveMessages > 0 && (
                        <span className="absolute -top-1 -right-1 w-4 h-4 bg-pink-500 rounded-full text-[10px] text-white flex items-center justify-center font-bold animate-pulse">
                          {unreadLoveMessages > 9 ? '9+' : unreadLoveMessages}
                        </span>
                      )}
                    </Button>
                    <Link to="/courage-deck">
                      <Button variant="outline" size="sm" className="rounded-full border-pink-500/30 text-pink-600 hover:bg-pink-500/10 hover:text-pink-700 h-9 w-9 sm:h-auto sm:w-auto p-2 sm:px-3" data-testid="quick-game-btn">
                        <MessageSquare className="h-4 w-4" />
                        <span className="hidden sm:inline ml-2">Game</span>
                      </Button>
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Personalized For You Feed */}
        <div className="mb-6">
          <ForYouFeed />
        </div>

        {/* Gentle reminders subscription nudge — auto-hidden once enabled or dismissed for 7d */}
        <div className="mb-6">
          <PushSubscribeNudge />
        </div>

        {/* Daily Collective Intention - Global manifestation */}
        <div className="mb-6">
          <CollectiveIntention />
          <BirthdayCelebration />
        </div>

        {/* Legacy DailyStreak card removed — unified Daily Ritual Streaks card below is the single source of truth (iter 96) */}

        {/* Wellness Snapshot - Shows if quiz completed */}
        {user?.quiz_completed && user?.wellness_profile && (
          <Card className="mb-6 bg-gradient-to-br from-violet-500/5 via-purple-500/5 to-indigo-500/5 border-violet-500/20 animate-fade-in overflow-hidden" data-testid="wellness-snapshot-card">
            <CardContent className="p-4 sm:p-5">
              {/* Header - Mobile optimized */}
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center shadow-lg shadow-violet-500/20 flex-shrink-0">
                    <Sparkles className="h-4 w-4 sm:h-5 sm:w-5 text-white" />
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-violet-700 dark:text-violet-300">Your Cosmic Blueprint</h3>
                    <p className="text-[11px] text-muted-foreground">Know thyself, heal thyself</p>
                  </div>
                </div>
                <div className="flex items-center gap-2 ml-12 sm:ml-0">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="h-8 text-xs text-violet-600 hover:text-violet-700 border-violet-300 hover:border-violet-400 gap-1.5 rounded-full px-3"
                    onClick={async () => {
                      try {
                        toast.loading("Generating your birth chart...");
                        const response = await axios.get(`${API}/birth-chart/download`, {
                          headers: getAuthHeader(),
                          responseType: 'blob'
                        });
                        
                        const blob = new Blob([response.data], { type: 'application/pdf' });
                        const url = window.URL.createObjectURL(blob);
                        const filename = `birth_chart_${user.name?.replace(/\s/g, '_').toLowerCase() || 'user'}.pdf`;
                        
                        // Check if on iOS/Safari which has download issues
                        const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
                        const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
                        
                        if (isIOS || isSafari) {
                          // On iOS/Safari, open in new tab - user can save from there
                          const newWindow = window.open(url, '_blank');
                          if (newWindow) {
                            toast.dismiss();
                            toast.success("PDF opened! Tap the share button to save.", { duration: 5000 });
                          } else {
                            // Popup blocked - try direct navigation
                            window.location.href = url;
                            toast.dismiss();
                            toast.success("PDF ready! Save it from your browser.");
                          }
                        } else {
                          // Desktop browsers - use download link
                          const link = document.createElement('a');
                          link.href = url;
                          link.setAttribute('download', filename);
                          document.body.appendChild(link);
                          link.click();
                          link.remove();
                          toast.dismiss();
                          toast.success("Birth chart downloaded!");
                        }
                        
                        // Cleanup after delay
                        setTimeout(() => window.URL.revokeObjectURL(url), 10000);
                      } catch (error) {
                        toast.dismiss();
                        toast.error("Failed to download birth chart");
                        console.error(error);
                      }
                    }}
                    data-testid="download-birth-chart-btn"
                  >
                    <Download className="h-3.5 w-3.5" />
                    <span className="hidden xs:inline">Full</span> Chart
                  </Button>
                  <Link to="/quiz">
                    <Button variant="ghost" size="sm" className="h-8 text-xs text-muted-foreground hover:text-violet-600 rounded-full px-2">
                      Edit
                    </Button>
                  </Link>
                </div>
              </div>
              
              {/* Astrology Section - Big Three */}
              <div className="mb-3">
                <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium mb-2 px-0.5">Your Big Three</p>
                <div className="grid grid-cols-3 gap-1.5 sm:gap-2">
                  {/* Sun Sign */}
                  <div className="relative p-2.5 sm:p-3 bg-gradient-to-br from-amber-500/10 to-orange-500/10 rounded-xl border border-amber-500/20 text-center group hover:border-amber-500/40 transition-colors" data-testid="sun-sign-card">
                    <div className="absolute top-1 right-1 w-1.5 h-1.5 bg-amber-500 rounded-full opacity-60"></div>
                    <p className="text-xl sm:text-2xl mb-0.5">{user.wellness_profile.sun_sign?.symbol || '☀️'}</p>
                    <p className="text-xs sm:text-sm font-semibold text-amber-700 dark:text-amber-400 truncate">{user.wellness_profile.sun_sign?.sign || 'Unknown'}</p>
                    <p className="text-[8px] sm:text-[9px] text-amber-600/70 dark:text-amber-500/70 font-medium">SUN</p>
                  </div>
                  {/* Moon Sign */}
                  <div className={`relative p-2.5 sm:p-3 rounded-xl border text-center transition-colors ${user.wellness_profile.moon_sign ? 'bg-gradient-to-br from-indigo-500/10 to-blue-500/10 border-indigo-500/20 hover:border-indigo-500/40' : 'bg-slate-500/5 border-dashed border-slate-500/20'}`} data-testid="moon-sign-card">
                    {user.wellness_profile.moon_sign ? (
                      <>
                        <div className="absolute top-1 right-1 w-1.5 h-1.5 bg-indigo-500 rounded-full opacity-60"></div>
                        <p className="text-xl sm:text-2xl mb-0.5">{user.wellness_profile.moon_sign.symbol}</p>
                        <p className="text-xs sm:text-sm font-semibold text-indigo-700 dark:text-indigo-400 truncate">{user.wellness_profile.moon_sign.sign}</p>
                        <p className="text-[8px] sm:text-[9px] text-indigo-600/70 dark:text-indigo-500/70 font-medium">MOON</p>
                      </>
                    ) : (
                      <>
                        <p className="text-xl sm:text-2xl mb-0.5 opacity-30">☽</p>
                        <p className="text-[10px] text-muted-foreground">Add time</p>
                        <p className="text-[8px] sm:text-[9px] text-slate-500/70 font-medium">MOON</p>
                      </>
                    )}
                  </div>
                  {/* Rising Sign */}
                  <div className={`relative p-2.5 sm:p-3 rounded-xl border text-center transition-colors ${user.wellness_profile.rising_sign ? 'bg-gradient-to-br from-rose-500/10 to-pink-500/10 border-rose-500/20 hover:border-rose-500/40' : 'bg-slate-500/5 border-dashed border-slate-500/20'}`} data-testid="rising-sign-card">
                    {user.wellness_profile.rising_sign ? (
                      <>
                        <div className="absolute top-1 right-1 w-1.5 h-1.5 bg-rose-500 rounded-full opacity-60"></div>
                        <p className="text-xl sm:text-2xl mb-0.5">{user.wellness_profile.rising_sign.symbol}</p>
                        <p className="text-xs sm:text-sm font-semibold text-rose-700 dark:text-rose-400 truncate">{user.wellness_profile.rising_sign.sign}</p>
                        <p className="text-[8px] sm:text-[9px] text-rose-600/70 dark:text-rose-500/70 font-medium">RISING</p>
                      </>
                    ) : (
                      <>
                        <p className="text-xl sm:text-2xl mb-0.5 opacity-30">↑</p>
                        <p className="text-[10px] text-muted-foreground">Add time</p>
                        <p className="text-[8px] sm:text-[9px] text-slate-500/70 font-medium">RISING</p>
                      </>
                    )}
                  </div>
                </div>
              </div>
              
              {/* Numerology & Spiritual Section */}
              <div className="grid grid-cols-3 gap-1.5 sm:gap-2">
                {/* Life Path */}
                {user.wellness_profile.life_path && (
                  <div className="p-2.5 sm:p-3 bg-gradient-to-br from-violet-500/10 to-purple-500/10 rounded-xl border border-violet-500/20 text-center" data-testid="life-path-card">
                    <p className="text-xl sm:text-2xl font-bold text-violet-600 dark:text-violet-400">{user.wellness_profile.life_path.number}</p>
                    <p className="text-[8px] sm:text-[9px] text-violet-600/70 dark:text-violet-500/70 font-medium uppercase">Life Path</p>
                    <p className="text-[9px] sm:text-[10px] text-muted-foreground mt-0.5 truncate">{user.wellness_profile.life_path.title}</p>
                  </div>
                )}
                {/* Spirit Guide */}
                {user.wellness_profile.animal_guide && (
                  <div className="p-2.5 sm:p-3 bg-gradient-to-br from-emerald-500/10 to-teal-500/10 rounded-xl border border-emerald-500/20 text-center" data-testid="spirit-guide-card">
                    <p className="text-xl sm:text-2xl mb-0.5">{user.wellness_profile.animal_guide.emoji || '🦋'}</p>
                    <p className="text-xs sm:text-sm font-semibold text-emerald-600 dark:text-emerald-400 truncate">{user.wellness_profile.animal_guide.name}</p>
                    <p className="text-[8px] sm:text-[9px] text-emerald-600/70 dark:text-emerald-500/70 font-medium uppercase">Spirit Guide</p>
                  </div>
                )}
                {/* Angel Number */}
                {user.wellness_profile.angel_number && (
                  <div className="p-2.5 sm:p-3 bg-gradient-to-br from-rose-500/10 to-pink-500/10 rounded-xl border border-rose-500/20 text-center" data-testid="angel-number-card">
                    <p className="text-lg sm:text-xl font-bold text-rose-600 dark:text-rose-400">{user.wellness_profile.angel_number.number}</p>
                    <p className="text-[8px] sm:text-[9px] text-rose-600/70 dark:text-rose-500/70 font-medium uppercase">Angel #</p>
                    <p className="text-[9px] sm:text-[10px] text-muted-foreground mt-0.5 leading-tight">{user.wellness_profile.angel_number.meaning}</p>
                  </div>
                )}
              </div>

              {/* Soul Connection / Relationship Type Section */}
              {soulConnectionQuiz && (
                (() => {
                  const result = soulConnectionQuiz;
                  const colorMap = {
                    twinFlame: 'from-rose-500/10 to-orange-500/10 border-rose-500/30',
                    soulmate: 'from-pink-500/10 to-rose-500/10 border-pink-500/30',
                    karmic: 'from-purple-500/10 to-indigo-500/10 border-purple-500/30'
                  };
                  const textColorMap = {
                    twinFlame: 'text-rose-600 dark:text-rose-400',
                    soulmate: 'text-pink-600 dark:text-pink-400',
                    karmic: 'text-purple-600 dark:text-purple-400'
                  };
                  const subtitleMap = {
                    twinFlame: 'Mirror Soul Journey',
                    soulmate: 'Soul Family Bond',
                    karmic: 'Karmic Lesson'
                  };
                  return (
                    <div className="mt-3">
                      <div className="flex items-center justify-between mb-2 px-0.5">
                        <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">Your Relationship Type</p>
                        <button 
                          onClick={() => setShowSoulQuizModal(true)}
                          className="text-[10px] text-violet-500 hover:text-violet-600 font-medium"
                          data-testid="retake-soul-quiz-btn"
                        >
                          Retake Quiz
                        </button>
                      </div>
                      <div className={`p-3 bg-gradient-to-br ${colorMap[result.type]} rounded-xl border flex items-center gap-3`} data-testid="soul-connection-card">
                        <div className="text-3xl flex-shrink-0">{result.emoji}</div>
                        <div className="flex-1 min-w-0">
                          <p className={`text-sm font-semibold ${textColorMap[result.type]} truncate`}>{result.title}</p>
                          <p className="text-[10px] text-muted-foreground mt-0.5">{subtitleMap[result.type]}</p>
                        </div>
                      </div>
                    </div>
                  );
                })()
              )}

              {/* Take Soul Connection Quiz - Shows if no result yet */}
              {!soulConnectionQuiz && (
                <div className="mt-3">
                  <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium mb-2 px-0.5">Your Relationship Type</p>
                  <button 
                    onClick={() => setShowSoulQuizModal(true)}
                    className="w-full p-3 bg-gradient-to-br from-violet-500/10 to-pink-500/10 rounded-xl border border-violet-500/30 hover:border-violet-500/50 transition-colors text-left"
                    data-testid="take-soul-quiz-btn"
                  >
                    <div className="flex items-center gap-3">
                      <div className="text-2xl">💫</div>
                      <div className="flex-1">
                        <p className="text-sm font-semibold text-violet-600 dark:text-violet-400">Discover Your Connection Type</p>
                        <p className="text-[10px] text-muted-foreground">Take the Soul Connection Quiz</p>
                      </div>
                      <ChevronRight className="h-4 w-4 text-violet-500" />
                    </div>
                  </button>
                </div>
              )}

              {/* Share Your Cosmic Blueprint */}
              <div className="mt-4 pt-3 border-t border-violet-500/10">
                <Button
                  variant="ghost"
                  size="sm"
                  className="w-full h-9 text-xs text-violet-600 hover:text-violet-700 hover:bg-violet-500/10 gap-2 rounded-full"
                  onClick={async () => {
                    const appUrl = "https://www.stayrootedlala.com";
                    const spiritGuideEmoji = user.wellness_profile.animal_guide?.emoji || '🦋';
                    const shareText = `✨ My Cosmic Blueprint ✨\n\n☀️ Sun: ${user.wellness_profile.sun_sign?.sign || 'Unknown'}\n🌙 Moon: ${user.wellness_profile.moon_sign?.sign || 'Add birth time'}\n⬆️ Rising: ${user.wellness_profile.rising_sign?.sign || 'Add birth time'}\n🔢 Life Path: ${user.wellness_profile.life_path?.number || ''} - ${user.wellness_profile.life_path?.title || ''}\n${spiritGuideEmoji} Spirit Guide: ${user.wellness_profile.animal_guide?.name || ''}\n👼🏾 Angel Number: ${user.wellness_profile.angel_number?.number || ''}\n\n💜 Discover your cosmic blueprint at Lala Land!\n🔗 ${appUrl}`;
                    
                    if (navigator.share) {
                      try {
                        await navigator.share({
                          title: 'My Cosmic Blueprint - Lala Land',
                          text: shareText,
                          url: appUrl
                        });
                        toast.success("Shared your cosmic blueprint! 💫");
                      } catch (err) {
                        if (err.name !== 'AbortError') {
                          // Fallback to clipboard
                          await navigator.clipboard.writeText(shareText);
                          toast.success("Copied to clipboard! Share it with friends 💜");
                        }
                      }
                    } else {
                      await navigator.clipboard.writeText(shareText);
                      toast.success("Copied to clipboard! Share it with friends 💜");
                    }
                  }}
                  data-testid="share-cosmic-blueprint-btn"
                >
                  <Share2 className="h-3.5 w-3.5" />
                  Share Your Cosmic Blueprint
                </Button>
              </div>

              {/* Recommended Readables Based on Profile */}
              <div className="mt-4 pt-3 border-t border-violet-500/10">
                <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium mb-2 px-0.5">Recommended For You</p>
                <div className="space-y-2">
                  {/* Generate recommendations based on user's profile.
                      Rule the user asked for: NEVER recommend another person's
                      Life Path book. Only the user's own Life Path number maps to
                      a Life Path readable, and when present, it's pinned first
                      and gets a distinct "your number" treatment so the user
                      understands its importance.  Second tile is interest-based
                      (sun-sign element, with a universal fallback) and is never
                      a Life Path book — keeping the lane clear that Life Path
                      readables are personal, not browseable. */}
                  {(() => {
                    const recommendations = [];
                    const sunSign = user.wellness_profile.sun_sign?.sign?.toLowerCase();
                    const lifePath = user.wellness_profile.life_path?.number;
                    const lifePathTitle = user.wellness_profile.life_path?.title;

                    // 1. PINNED: the user's OWN Life Path readable (any of 1-9, 11, 22, 33).
                    const SUPPORTED_LIFE_PATHS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 11, 22, 33];
                    if (SUPPORTED_LIFE_PATHS.includes(lifePath)) {
                      recommendations.push({
                        bookId: `life-path-${lifePath}`,
                        title: `Life Path ${lifePath}${lifePathTitle ? `: ${lifePathTitle}` : ''}`,
                        emoji: '🔢',
                        reason: 'Your number — your blueprint',
                        pinned: true,
                      });
                    }

                    // 2. Interest-based — sun sign element. Never a Life Path book.
                    if (['aries', 'leo', 'sagittarius'].includes(sunSign)) {
                      recommendations.push({ bookId: 'manifestation-magic', title: 'Manifestation Magic', emoji: '🔥', reason: 'For your fire sign energy' });
                    } else if (['taurus', 'virgo', 'capricorn'].includes(sunSign)) {
                      recommendations.push({ bookId: 'energy-healing', title: 'Energy & Vibration', emoji: '🌿', reason: 'For grounding your earth energy' });
                    } else if (['gemini', 'libra', 'aquarius'].includes(sunSign)) {
                      recommendations.push({ bookId: 'intuition-guide', title: 'Trust Your Intuition', emoji: '💨', reason: 'For your air sign wisdom' });
                    } else if (['cancer', 'scorpio', 'pisces'].includes(sunSign)) {
                      recommendations.push({ bookId: 'meditation-mastery', title: 'Meditation Mastery', emoji: '💧', reason: 'For your water sign depth' });
                    }

                    // 3. Universal fallback so we always show 2 tiles.
                    if (recommendations.length < 2) {
                      recommendations.push({ bookId: 'ego-death', title: 'Ego Death', emoji: '🦋', reason: 'Transform & evolve' });
                    }

                    return recommendations.slice(0, 2).map((rec, idx) => (
                      <Link
                        key={idx}
                        to={`/readables?book=${rec.bookId}`}
                        className={`flex items-center gap-3 p-2 rounded-lg transition-colors group ${
                          rec.pinned
                            ? 'bg-gradient-to-r from-amber-500/10 via-violet-500/10 to-pink-500/10 border border-violet-400/30 hover:from-amber-500/15 hover:via-violet-500/15 hover:to-pink-500/15'
                            : 'bg-violet-500/5 hover:bg-violet-500/10'
                        }`}
                        data-testid={`recommended-tile-${rec.bookId}`}
                      >
                        <span className="text-lg">{rec.emoji}</span>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5">
                            <p className="text-xs font-medium text-primary truncate">{rec.title}</p>
                            {rec.pinned && (
                              <span className="text-[8px] uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-violet-500 text-white font-semibold flex-shrink-0">
                                Yours
                              </span>
                            )}
                          </div>
                          <p className="text-[10px] text-muted-foreground">{rec.reason}</p>
                        </div>
                        <ChevronRight className="h-4 w-4 text-muted-foreground group-hover:text-violet-500 transition-colors flex-shrink-0" />
                      </Link>
                    ));
                  })()}
                </div>

                {/* Why your Life Path matters — micro-copy that only shows when
                    the user actually has a number to anchor it to. Reinforces
                    the importance of knowing their OWN number per the brand
                    intent. */}
                {user.wellness_profile.life_path?.number && [1, 2, 3, 4, 5, 6, 7, 8, 9, 11, 22, 33].includes(user.wellness_profile.life_path.number) && (
                  <p className="mt-2 text-[10px] text-muted-foreground italic px-0.5 leading-relaxed">
                    Your Life Path number is the lens you came in with. Reading{' '}
                    <span className="text-violet-600 dark:text-violet-400 font-medium">your own</span>{' '}
                    is how you understand your gifts, blocks, and timing — no one else's number tells your story.
                  </p>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Your Body Type Profile - Shows after Cosmic Blueprint if nutrition quiz completed */}
        {nutritionProfile?.body_type && BODY_TYPE_INFO[nutritionProfile.body_type] && (
          <Card className="mb-6 bg-gradient-to-br from-emerald-500/10 via-teal-500/10 to-cyan-500/10 border-emerald-500/30" data-testid="body-type-profile-card">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Salad className="w-4 h-4 text-emerald-500" />
                  <h3 className="text-sm font-semibold text-emerald-700 dark:text-emerald-300">Your Wellness Snapshot</h3>
                </div>
                <Link to="/nutrition">
                  <Button variant="ghost" size="sm" className="h-6 text-xs text-emerald-600">
                    View Nutrition →
                  </Button>
                </Link>
              </div>
              
              {/* Body Type + Dosha Side by Side */}
              <div className="grid grid-cols-2 gap-3 mb-3">
                {/* Body Type */}
                <div className="flex items-center gap-2 p-2 rounded-lg bg-background/60">
                  <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center shadow">
                    <span className="text-lg">{BODY_TYPE_INFO[nutritionProfile.body_type].emoji}</span>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Body Type</p>
                    <p className="text-sm font-semibold text-primary">{BODY_TYPE_INFO[nutritionProfile.body_type].name}</p>
                  </div>
                </div>
                
                {/* Dosha */}
                {nutritionProfile.dosha && DOSHA_INFO[nutritionProfile.dosha] ? (
                  <div className="flex items-center gap-2 p-2 rounded-lg bg-background/60">
                    <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${DOSHA_INFO[nutritionProfile.dosha].color} flex items-center justify-center shadow`}>
                      <span className="text-lg">{DOSHA_INFO[nutritionProfile.dosha].emoji}</span>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Dosha</p>
                      <p className="text-sm font-semibold text-primary">{DOSHA_INFO[nutritionProfile.dosha].name}</p>
                    </div>
                  </div>
                ) : (
                  <Link to="/nutrition" className="flex items-center gap-2 p-2 rounded-lg bg-background/60 border border-dashed border-muted-foreground/30 hover:border-primary/50 transition-colors">
                    <div className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center">
                      <span className="text-lg">✨</span>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Dosha</p>
                      <p className="text-xs font-medium text-primary">Discover Yours →</p>
                    </div>
                  </Link>
                )}
              </div>
              
              {/* Traits */}
              <div className="mb-3">
                <div className="flex flex-wrap gap-1.5">
                  {BODY_TYPE_INFO[nutritionProfile.body_type].traits.map((trait, i) => (
                    <span key={i} className="text-xs px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-700 dark:text-emerald-300">
                      ✓ {trait}
                    </span>
                  ))}
                </div>
              </div>
              
              {/* Quick Stats from Quiz */}
              <div className="grid grid-cols-3 gap-2 p-2 rounded-lg bg-background/60">
                <div className="text-center">
                  <p className="text-lg font-bold text-primary">{nutritionProfile.daily_calories}</p>
                  <p className="text-[10px] text-muted-foreground">Daily Cal</p>
                </div>
                <div className="text-center border-x border-border">
                  <p className="text-lg font-bold text-primary">{nutritionProfile.macro_split?.protein}%</p>
                  <p className="text-[10px] text-muted-foreground">Protein</p>
                </div>
                <div className="text-center">
                  <p className="text-lg font-bold text-primary capitalize">{nutritionProfile.primary_goal?.replace('_', ' ')}</p>
                  <p className="text-[10px] text-muted-foreground">Goal</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
        
        {/* Nutrition Quiz Prompt - Shows if nutrition quiz not completed */}
        {!nutritionProfile && user?.quiz_completed && (
          <Card className="mb-6 bg-gradient-to-r from-emerald-500/10 to-teal-500/10 border-emerald-500/30" data-testid="nutrition-quiz-prompt">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center">
                    <Salad className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-primary">Discover Your Body Type</h3>
                    <p className="text-sm text-muted-foreground">Take our wellness quiz for personalized nutrition</p>
                  </div>
                </div>
                <Link to="/nutrition">
                  <Button size="sm" className="bg-gradient-to-r from-emerald-500 to-teal-500 text-white rounded-full">
                    Take Quiz
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        )}
        {!user?.quiz_completed && !user?.quiz_skipped && (
          <Card className="mb-6 bg-gradient-to-r from-emerald-500/10 to-teal-500/10 border-emerald-500/30 animate-fade-in" data-testid="quiz-prompt-card">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center">
                    <Sparkles className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-primary">Personalize Your Journey</h3>
                    <p className="text-sm text-muted-foreground">Take a quick quiz to unlock personalized recommendations</p>
                  </div>
                </div>
                <Link to="/quiz">
                  <Button className="bg-gradient-to-r from-emerald-500 to-teal-500 text-white rounded-full">
                    Start Quiz
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Featured Black-Owned Business — refreshes daily, deterministic per user */}
        {(() => {
          const featured = getFeaturedBusiness(user?.id || "");
          return (
            <Card className="mb-6 relative overflow-hidden border-0 shadow-xl" data-testid="featured-business-card">
              {/* Animated gradient background */}
              <div className="absolute inset-0 bg-gradient-to-br from-violet-600 via-purple-600 to-pink-600 dark:from-violet-900 dark:via-purple-900 dark:to-pink-900" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />
              
              {/* Decorative elements */}
              <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-white/10 to-transparent rounded-full -translate-y-1/2 translate-x-1/2 blur-2xl" />
              <div className="absolute bottom-0 left-0 w-24 h-24 bg-gradient-to-tr from-pink-500/20 to-transparent rounded-full translate-y-1/2 -translate-x-1/2 blur-xl" />
              
              <CardContent className="relative p-5 z-10">
                {/* Header */}
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-2xl bg-white/20 dark:bg-white/10 backdrop-blur-sm flex items-center justify-center shadow-lg ring-2 ring-white/30">
                      <span className="text-2xl">{featured.emoji}</span>
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-bold text-white drop-shadow-md">
                          {featured.isNewBrand ? "✨ Let Us Put You On" : "💛 Featured Black-Owned"}
                        </span>
                        {featured.isNewBrand && (
                          <span className="text-[10px] bg-white/20 backdrop-blur-sm text-white px-2 py-0.5 rounded-full font-medium">Up & Coming</span>
                        )}
                      </div>
                      <p className="text-xs text-white/70">Ships Nationwide • A new brand each day 🌅</p>
                    </div>
                  </div>
                </div>
                
                {/* Content */}
                <div className="flex flex-col sm:flex-row sm:items-end gap-4">
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold text-white mb-1 drop-shadow-md">{featured.name}</h3>
                    <p className="text-sm text-white/90 italic mb-2">{featured.tagline}</p>
                    <p className="text-xs text-white/80 mb-3 leading-relaxed">{featured.description}</p>
                    
                    {/* Tags */}
                    <div className="flex items-center gap-2 flex-wrap mb-3">
                      <span className="text-[11px] bg-white/20 backdrop-blur-sm text-white px-3 py-1 rounded-full font-medium">{featured.category}</span>
                      {featured.products.slice(0, 3).map((product, idx) => (
                        <span key={idx} className="text-[11px] text-white/70">• {product}</span>
                      ))}
                    </div>
                    
                    <p className="text-xs text-white/70">Founded by <span className="font-semibold text-white">{featured.founder}</span></p>
                  </div>
                  
                  {/* CTA Button */}
                  <a href={featured.url} target="_blank" rel="noopener noreferrer" className="flex-shrink-0">
                    <Button className="bg-white text-purple-700 hover:bg-white/90 hover:scale-105 transition-all duration-300 rounded-full px-8 py-5 font-bold shadow-xl">
                      Shop Now →
                    </Button>
                  </a>
                </div>
              </CardContent>
            </Card>
          );
        })()}

        {/* Black History Month Banner - February */}
        {new Date().getMonth() === 1 && (
          <Card className="mb-8 bg-gradient-to-r from-red-600/20 via-yellow-500/20 to-green-600/20 border-yellow-500/30 animate-fade-in overflow-hidden">
            <CardContent className="p-6">
              <div className="flex flex-col md:flex-row items-center gap-4">
                <div className="flex-shrink-0 text-5xl">✊🏿✊🏾✊🏽</div>
                <div className="flex-1 text-center md:text-left">
                  <h2 className="text-xl font-bold text-primary mb-1">
                    Celebrating Black History Month
                  </h2>
                  <p className="text-sm text-muted-foreground mb-3">
                    Honoring the rich heritage, culture, and achievements of Black Americans. 
                    Experience powerful spoken word, soulful music, and stories of resilience.
                  </p>
                  <div className="flex flex-wrap gap-2 justify-center md:justify-start">
                    <Link to="/music?video=gpMxTa4M004">
                      <Button size="sm" className="rounded-full bg-gradient-to-r from-red-600 to-yellow-500 hover:opacity-90 text-white">
                        🎤 "You So Black" Poetry
                      </Button>
                    </Link>
                    <Link to="/music?video=JqOqo50LSZ0">
                      <Button size="sm" className="rounded-full bg-gradient-to-r from-purple-600 to-pink-500 hover:opacity-90 text-white">
                        ✨ "Still I Rise"
                      </Button>
                    </Link>
                    <Link to="/music?video=OcuOSOgMC0E">
                      <Button variant="outline" size="sm" className="rounded-full border-green-500 text-green-600 dark:text-green-400">
                        🎷 Soul & Jazz
                      </Button>
                    </Link>
                    <a href="https://naspora.com" target="_blank" rel="noopener noreferrer">
                      <Button size="sm" className="rounded-full bg-black hover:bg-gray-800 text-white">
                        🛍️ Buy Black Directory
                      </Button>
                    </a>
                    <Link to="/readables">
                      <Button variant="outline" size="sm" className="rounded-full border-yellow-500 text-yellow-600 dark:text-yellow-400">
                        📚 Healing Books
                      </Button>
                    </Link>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Month of Love Banner - February (alongside BHM) */}
        {new Date().getMonth() === 1 && (
          <Card className="mb-8 bg-gradient-to-r from-pink-500/20 via-rose-500/20 to-red-500/20 border-pink-500/30 animate-fade-in overflow-hidden">
            <CardContent className="p-6">
              <div className="flex flex-col md:flex-row items-center gap-4">
                <div className="flex-shrink-0 text-5xl">💕💗💖</div>
                <div className="flex-1 text-center md:text-left">
                  <h2 className="text-xl font-bold text-primary mb-1">
                    Month of Love: Love Language Healing
                  </h2>
                  <p className="text-sm text-muted-foreground mb-3">
                    February is the month to heal all types of relationships — with yourself, your partner, 
                    family, and those who've caused you pain. Discover the art of holding space while honoring your journey.
                  </p>
                  <div className="flex flex-wrap gap-2 justify-center md:justify-start">
                    <Link to="/readables?category=Love%20Language%20Healing">
                      <Button size="sm" className="rounded-full bg-gradient-to-r from-pink-500 to-rose-500 hover:opacity-90 text-white">
                        💕 Love Language Series
                      </Button>
                    </Link>
                    <Link to="/affirmations?category=self-love">
                      <Button size="sm" className="rounded-full bg-gradient-to-r from-fuchsia-500 to-pink-500 hover:opacity-90 text-white">
                        💗 Self-Love Affirmations
                      </Button>
                    </Link>
                    <Link to="/readables">
                      <Button variant="outline" size="sm" className="rounded-full border-pink-500 text-pink-600 dark:text-pink-400">
                        📖 Healing Books
                      </Button>
                    </Link>
                    <Link to="/journal">
                      <Button variant="outline" size="sm" className="rounded-full border-rose-500 text-rose-600 dark:text-rose-400">
                        ✍🏾 Reflection Journal
                      </Button>
                    </Link>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* What's New This Week - Clean Mindful Redesign */}
        {weeklyFeatured && (
          <div className="mb-8 animate-fade-in" data-testid="whats-new-section">
            {/* Section Header */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center shadow-lg shadow-violet-500/20">
                  <Sparkles className="h-5 w-5 text-white" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-primary">What's New This Week</h2>
                  <p className="text-xs text-muted-foreground">Week {weeklyFeatured.week_number} • Curated for your journey</p>
                </div>
              </div>
            </div>
            
            {/* Featured Spotlight - Two Main Cards */}
            <div className="grid md:grid-cols-2 gap-4 mb-4">
              {/* Soul Circle Spotlight */}
              <Link to="/soul-circle" className="group">
                <div className="relative h-full bg-gradient-to-br from-violet-500/15 via-purple-500/10 to-pink-500/15 rounded-2xl p-5 border border-violet-500/30 hover:border-violet-500/50 transition-all duration-300 overflow-hidden">
                  <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-bl from-pink-500/20 to-transparent rounded-full blur-2xl -translate-y-1/2 translate-x-1/2"></div>
                  <div className="relative z-10">
                    <div className="flex items-center gap-2 mb-3">
                      <Users className="h-5 w-5 text-violet-500" />
                      <span className="text-xs font-semibold text-violet-500 uppercase tracking-wider">Connect</span>
                      <span className="ml-auto px-2.5 py-1 text-[10px] font-bold bg-gradient-to-r from-violet-500 to-pink-500 text-white rounded-full shadow-lg">NEW</span>
                    </div>
                    <h3 className="text-xl font-bold text-primary mb-2 group-hover:text-violet-600 dark:group-hover:text-violet-400 transition-colors">Soul Circle</h3>
                    <p className="text-sm text-muted-foreground mb-3">Connect with aligned souls and discover spiritual compatibility.</p>
                    <div className="flex items-center gap-2 text-xs text-violet-600 dark:text-violet-400 font-medium">
                      <span>Start connecting</span>
                      <ChevronRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </div>
                </div>
              </Link>

              {/* Courageous Conversations Game - Spotlight */}
              <Link to="/courage-deck" className="group">
                <div className="relative h-full bg-gradient-to-br from-rose-500/15 via-orange-500/10 to-amber-500/15 rounded-2xl p-5 border border-rose-500/30 hover:border-rose-500/50 transition-all duration-300 overflow-hidden">
                  <div className="absolute bottom-0 left-0 w-32 h-32 bg-gradient-to-tr from-amber-500/20 to-transparent rounded-full blur-2xl translate-y-1/2 -translate-x-1/4"></div>
                  <div className="relative z-10">
                    <div className="flex items-center gap-2 mb-3">
                      <MessageSquare className="h-5 w-5 text-rose-500" />
                      <span className="text-xs font-semibold text-rose-500 uppercase tracking-wider">Game</span>
                      <span className="ml-auto px-2.5 py-1 text-[10px] font-bold bg-gradient-to-r from-rose-500 to-orange-500 text-white rounded-full shadow-lg">PLAY</span>
                    </div>
                    <h3 className="text-xl font-bold text-primary mb-2 group-hover:text-rose-600 dark:group-hover:text-rose-400 transition-colors">Courageous Conversations</h3>
                    <p className="text-sm text-muted-foreground mb-3">Transform relationships through meaningful dialogue. Play face-to-face!</p>
                    <div className="flex items-center gap-2 text-xs text-rose-600 dark:text-rose-400 font-medium">
                      <span>Play now</span>
                      <ChevronRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </div>
                </div>
              </Link>
            </div>

            {/* Quick Links - Simplified (No Duplicates) */}
            <div className="grid grid-cols-3 gap-3">
              {/* Weekly Affirmation */}
              <Link to={weeklyFeatured.featured_affirmation_theme?.category ? `/affirmations?category=${weeklyFeatured.featured_affirmation_theme.category}` : "/affirmations"} className="group">
                <div className="bg-gradient-to-br from-violet-500/8 to-purple-500/8 rounded-xl p-3 border border-violet-500/20 hover:border-violet-500/40 hover:bg-violet-500/10 transition-all duration-300">
                  <div className="w-9 h-9 rounded-xl bg-violet-500/20 flex items-center justify-center mb-2 group-hover:scale-110 transition-transform">
                    <Heart className="h-4 w-4 text-violet-500" />
                  </div>
                  <p className="text-[11px] font-semibold text-primary leading-tight">Affirmation</p>
                  <p className="text-[10px] text-muted-foreground">{weeklyFeatured.featured_affirmation_theme?.theme || "Self Love"}</p>
                </div>
              </Link>

              {/* NPR Tiny Desk - Only Music Link */}
              <Link to="/music?video=4cfmEgpOOZk" className="group">
                <div className="bg-gradient-to-br from-amber-500/8 to-orange-500/8 rounded-xl p-3 border border-amber-500/20 hover:border-amber-500/40 hover:bg-amber-500/10 transition-all duration-300">
                  <div className="w-9 h-9 rounded-xl bg-amber-500/20 flex items-center justify-center mb-2 group-hover:scale-110 transition-transform">
                    <Music className="h-4 w-4 text-amber-500" />
                  </div>
                  <p className="text-[11px] font-semibold text-primary leading-tight">Tiny Desk</p>
                  <p className="text-[10px] text-muted-foreground">NPR Vibes</p>
                </div>
              </Link>

              {/* Nourish */}
              <Link to="/nutrition" className="group">
                <div className="bg-gradient-to-br from-emerald-500/8 to-teal-500/8 rounded-xl p-3 border border-emerald-500/20 hover:border-emerald-500/40 hover:bg-emerald-500/10 transition-all duration-300">
                  <div className="w-9 h-9 rounded-xl bg-emerald-500/20 flex items-center justify-center mb-2 group-hover:scale-110 transition-transform">
                    <Salad className="h-4 w-4 text-emerald-500" />
                  </div>
                  <p className="text-[11px] font-semibold text-primary leading-tight">Nourish</p>
                  <p className="text-[10px] text-muted-foreground">Healthy Eats</p>
                </div>
              </Link>
            </div>

            {/* Readables Banner - Part of What's New */}
            <Link to="/readables" className="block mt-4">
              <div className="bg-gradient-to-r from-violet-500/15 via-purple-500/15 to-indigo-500/15 border border-violet-500/30 rounded-xl p-4 hover:border-violet-500/50 transition-all group">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center flex-shrink-0 group-hover:scale-105 transition-transform">
                    <BookText className="h-6 w-6 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                      <h3 className="text-sm font-bold text-primary">Readables</h3>
                      <span className="text-[9px] bg-violet-500 text-white px-1.5 py-0.5 rounded-full">NEW</span>
                    </div>
                    <p className="text-xs text-muted-foreground">Mini books for spiritual growth ✨</p>
                  </div>
                  <ChevronRight className="h-5 w-5 text-violet-500 group-hover:translate-x-1 transition-transform flex-shrink-0" />
                </div>
              </div>
            </Link>

            {/* Let's Talk About It Banner - Part of What's New */}
            <Link to="/music?tab=lets-talk" className="block mt-3">
              <div className="bg-gradient-to-r from-teal-500/15 via-cyan-500/15 to-blue-500/15 border border-teal-500/30 rounded-xl p-4 hover:border-teal-500/50 transition-all group">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-teal-500 to-cyan-600 flex items-center justify-center flex-shrink-0 group-hover:scale-105 transition-transform">
                    <MessageSquare className="h-6 w-6 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                      <h3 className="text-sm font-bold text-primary">Let's Talk About It</h3>
                      <span className="text-[9px] bg-teal-500 text-white px-1.5 py-0.5 rounded-full">HEALING</span>
                    </div>
                    <p className="text-xs text-muted-foreground">Real stories, real healing 💬</p>
                  </div>
                  <ChevronRight className="h-5 w-5 text-teal-500 group-hover:translate-x-1 transition-transform flex-shrink-0" />
                </div>
              </div>
            </Link>
          </div>
        )}

        {/* Mood Tracker & Progress Section - Moved Higher */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
          {/* Mood Tracker */}
          <div className="animate-fade-in">
            <MoodTracker entries={journalEntries} isPremium={user?.is_premium} />
          </div>

          {/* Journey Progress for non-premium users only */}
          {!user?.is_premium && journeyProgress && (
            <div className="animate-fade-in">
              <JourneyProgressCard progress={journeyProgress} compact />
            </div>
          )}
        </div>

        {/* Morning & Night Routine Quick Access */}
        <div className="grid grid-cols-2 gap-4 animate-fade-in mt-6 mb-6">
          <Link to="/morning" className="block">
            <Card className="bg-gradient-to-br from-amber-100 to-orange-100 dark:from-amber-500/20 dark:to-orange-500/20 border-amber-200/50 dark:border-amber-500/30 hover:border-amber-400 dark:hover:border-amber-500/50 hover:shadow-lg transition-all duration-300 cursor-pointer group">
              <CardContent className="p-4 md:p-6">
                <div className="flex items-center gap-3 md:gap-4">
                  <div className="w-12 h-12 md:w-14 md:h-14 rounded-2xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center group-hover:scale-110 transition-transform shadow-lg">
                    <Sunrise className="h-6 w-6 md:h-7 md:w-7 text-white" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-base md:text-lg font-semibold text-primary">Morning Routine</h3>
                    <p className="text-xs md:text-sm text-muted-foreground">Start your day right</p>
                  </div>
                  <span className="text-amber-500 group-hover:translate-x-1 transition-transform text-lg">→</span>
                </div>
              </CardContent>
            </Card>
          </Link>

          <Link to="/bedtime" className="block">
            <Card className="bg-gradient-to-br from-indigo-100 to-purple-100 dark:from-indigo-500/20 dark:to-purple-500/20 border-indigo-200/50 dark:border-indigo-500/30 hover:border-indigo-400 dark:hover:border-indigo-500/50 hover:shadow-lg transition-all duration-300 cursor-pointer group">
              <CardContent className="p-4 md:p-6">
                <div className="flex items-center gap-3 md:gap-4">
                  <div className="w-12 h-12 md:w-14 md:h-14 rounded-2xl bg-gradient-to-br from-indigo-400 to-purple-500 flex items-center justify-center group-hover:scale-110 transition-transform shadow-lg">
                    <Bed className="h-6 w-6 md:h-7 md:w-7 text-white" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-base md:text-lg font-semibold text-primary">Night Routine</h3>
                    <p className="text-xs md:text-sm text-muted-foreground">Wind down peacefully</p>
                  </div>
                  <span className="text-indigo-500 group-hover:translate-x-1 transition-transform text-lg">→</span>
                </div>
              </CardContent>
            </Card>
          </Link>
        </div>

        {/* Daily Ritual Streaks — Unified Card */}
        {weeklySummary && weeklySummary.today_rituals && (() => {
          const rituals = [
            { key: "vibe_check", label: "Vibe Check", href: "/vibe-check", icon: Smile, color: "text-pink-500", bg: "bg-pink-500/10" },
            { key: "affirmation", label: "Affirmation", href: "/affirmations", icon: Sparkles, color: "text-amber-500", bg: "bg-amber-500/10" },
            { key: "meditation", label: "Meditation", href: "/guided-meditations", icon: CloudMoon, color: "text-violet-500", bg: "bg-violet-500/10" },
            { key: "journal", label: "Journal", href: "/journal", icon: BookOpen, color: "text-emerald-500", bg: "bg-emerald-500/10" },
            { key: "meal", label: "Nourish", href: "/nutrition", icon: Apple, color: "text-orange-500", bg: "bg-orange-500/10" },
            { key: "exercise", label: "Movement", href: "/workout-challenges", icon: Activity, color: "text-blue-500", bg: "bg-blue-500/10" },
          ];
          const completed = rituals.filter(r => weeklySummary.today_rituals[r.key]).length;
          const total = rituals.length;
          const pct = Math.round((completed / total) * 100);
          const streak = weeklySummary.current_streak || 0;
          const longest = weeklySummary.longest_streak || 0;
          // Detect first-day souls — never logged a ritual ever. Replace the
          // sad "0 days · 0 of 6 completed · 0%" copy with a warm welcome row
          // that invites them to plant their first seed.
          const isFreshSoul = streak === 0 && completed === 0 && longest === 0;
          return (
            <Card
              className="mb-6 bg-gradient-to-br from-orange-50 via-rose-50 to-amber-50 dark:from-orange-500/10 dark:via-rose-500/10 dark:to-amber-500/10 border-orange-200/60 dark:border-orange-500/30 shadow-sm animate-fade-in"
              data-testid="daily-ritual-streaks-card"
            >
              <CardContent className="p-4 md:p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-lg bg-orange-500/20 flex items-center justify-center">
                      <Flame className="h-4 w-4 text-orange-600" />
                    </div>
                    <div>
                      <p className="text-xs uppercase tracking-[0.15em] text-orange-700 dark:text-orange-400 font-medium">Daily Rituals</p>
                      <p className="text-[11px] text-muted-foreground">
                        {isFreshSoul
                          ? "Tap any one to begin — your streak starts the moment you do"
                          : "Your unified wellness streak"}
                      </p>
                    </div>
                  </div>
                  {!isFreshSoul && (
                    <div className="flex items-center gap-1 bg-orange-500/20 text-orange-700 dark:text-orange-300 px-3 py-1.5 rounded-full text-sm font-semibold" data-testid="streak-badge">
                      <Flame className="h-3.5 w-3.5" />
                      {streak} <span className="text-xs font-normal">day{streak === 1 ? '' : 's'}</span>
                    </div>
                  )}
                  {isFreshSoul && (
                    <div className="flex items-center gap-1 bg-amber-500/20 text-amber-700 dark:text-amber-300 px-3 py-1.5 rounded-full text-xs font-semibold" data-testid="fresh-soul-badge">
                      <Sparkles className="h-3 w-3" /> Day 0
                    </div>
                  )}
                </div>

                {/* Progress bar — hidden for fresh souls (0% is demoralizing) */}
                {!isFreshSoul && (
                  <div className="mb-4" data-testid="daily-rituals-progress">
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="text-xs font-medium text-primary">
                        {completed === total ? "✨ All rituals complete — beautiful work!" : `${completed} of ${total} completed today`}
                      </span>
                      <span className="text-xs text-muted-foreground">{pct}%</span>
                    </div>
                    <div className="h-2 bg-white/60 dark:bg-white/10 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-gradient-to-r from-orange-400 via-rose-400 to-amber-400 transition-all duration-500 ease-out"
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                  </div>
                )}

                {/* Fresh soul invitation banner */}
                {isFreshSoul && (
                  <div
                    className="mb-4 p-3 rounded-xl bg-gradient-to-br from-amber-100/60 to-rose-100/60 dark:from-amber-500/10 dark:to-rose-500/10 border border-amber-300/40 dark:border-amber-500/30"
                    data-testid="fresh-soul-invitation"
                  >
                    <p className="text-xs text-amber-900 dark:text-amber-200 leading-relaxed">
                      <span className="font-semibold">Welcome, beautiful soul ✨</span>
                      <br />
                      Pick whichever feels softest right now. One small ritual is all today asks.
                    </p>
                  </div>
                )}

                {/* Ritual grid */}
                <div className="grid grid-cols-3 md:grid-cols-6 gap-2.5" data-testid="daily-rituals-grid">
                  {rituals.map(r => {
                    const done = !!weeklySummary.today_rituals[r.key];
                    const Icon = r.icon;
                    return (
                      <Link
                        key={r.key}
                        to={r.href}
                        data-testid={`ritual-${r.key}`}
                        className={`group flex flex-col items-center justify-center p-2.5 rounded-xl border transition-all hover:-translate-y-0.5 ${
                          done
                            ? "bg-white dark:bg-white/10 border-emerald-300/60 dark:border-emerald-500/40 shadow-sm"
                            : "bg-white/40 dark:bg-white/5 border-border/40 hover:border-primary/30"
                        }`}
                      >
                        <div className={`relative w-9 h-9 rounded-full flex items-center justify-center mb-1.5 ${r.bg}`}>
                          <Icon className={`h-4 w-4 ${r.color}`} />
                          {done && (
                            <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-emerald-500 text-white flex items-center justify-center text-[10px] font-bold shadow-sm">
                              ✓
                            </span>
                          )}
                        </div>
                        <span className={`text-[11px] font-medium text-center leading-tight ${done ? "text-primary" : "text-muted-foreground"}`}>
                          {r.label}
                        </span>
                      </Link>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          );
        })()}

        {/* Weekly Wellness Summary — replaced with Soul Weather + Daily Constellation (iter 109).
            The numeric stat grid (1 Meal / 2 Glasses / 3 Journal / 4 Exercise) felt dense & clinical;
            we now surface the same signals as a poetic pill + a living star-map. */}
        {weeklySummary && (
          <SoulWeather weather={weeklySummary.soul_weather} />
        )}
        {/* Constellation: always render so it doesn't flicker out during a
            refetch. The component handles undefined ritualsByDay gracefully. */}
        <DailyConstellation ritualsByDay={weeklySummary?.week_rituals_by_day} />

        {/* Today's Affirmation — removed per user feedback (duplicate with For You feed) */}
        {/* Vibe Out Section - Stress Relief Hub */}
        <Card 
          className="bg-gradient-to-br from-violet-500/10 via-purple-500/10 to-pink-500/10 border-violet-500/30 shadow-lg animate-fade-in mb-6 cursor-pointer hover:shadow-xl transition-all overflow-hidden"
          onClick={() => setShowVibeOutHub(true)}
          data-testid="vibe-out-section"
        >
          <CardContent className="p-4 md:p-6">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-500 to-pink-500 flex items-center justify-center shadow-lg">
                    <span className="text-xl">✨</span>
                  </div>
                  <div>
                    <h3 className="font-semibold text-primary">Vibe Out Hub</h3>
                    <p className="text-xs text-muted-foreground">Quick stress relief & relaxation</p>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2 mt-3">
                  <span className="text-[10px] bg-blue-500/20 text-blue-600 dark:text-blue-300 px-2 py-1 rounded-full flex items-center gap-1">
                    <Wind className="w-3 h-3" /> Breathing
                  </span>
                  <span className="text-[10px] bg-violet-500/20 text-violet-600 dark:text-violet-300 px-2 py-1 rounded-full">
                    ✨ Visuals
                  </span>
                  <span className="text-[10px] bg-amber-500/20 text-amber-600 dark:text-amber-300 px-2 py-1 rounded-full">
                    🔔 Sound Bath
                  </span>
                  <span className="text-[10px] bg-purple-500/20 text-purple-600 dark:text-purple-300 px-2 py-1 rounded-full">
                    💜 Affirmations
                  </span>
                </div>
              </div>
              <ChevronRight className="w-5 h-5 text-muted-foreground" />
            </div>
          </CardContent>
        </Card>

        {/* 12-tile quick-access grid — moved into ExploreDrawer bottom sheet for altar-scroll feel */}
        {/* Support Black-Owned Businesses - Quick Access */}
        <Card className="bg-gradient-to-r from-amber-500/10 via-orange-500/10 to-red-500/10 border-amber-500/30 animate-fade-in mb-6" data-testid="black-owned-quick-access">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center">
                <Heart className="h-4 w-4 text-white" />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-primary">Mindful Black & Ethnic-Owned Wellness</h3>
                <p className="text-xs text-muted-foreground">Holistic health, plant-based food & mindful fitness</p>
              </div>
            </div>
            <div className="grid grid-cols-4 gap-2 mb-2">
              <a href="https://eatokra.com" target="_blank" rel="noopener noreferrer" className="block">
                <div className="flex flex-col items-center p-2 bg-background/60 rounded-xl hover:bg-background/80 transition-colors text-center">
                  <span className="text-xl mb-1">🥗</span>
                  <p className="text-[10px] font-medium text-primary">EatOkra</p>
                  <p className="text-[8px] text-muted-foreground">Healthy Eats</p>
                </div>
              </a>
              <a href="https://blackyogateachersalliance.org/find-a-teacher/" target="_blank" rel="noopener noreferrer" className="block">
                <div className="flex flex-col items-center p-2 bg-background/60 rounded-xl hover:bg-background/80 transition-colors text-center">
                  <span className="text-xl mb-1">🧘🏾‍♀️</span>
                  <p className="text-[10px] font-medium text-primary">Black Yoga</p>
                  <p className="text-[8px] text-muted-foreground">Find Teachers</p>
                </div>
              </a>
              <a href="https://blackgirlsbreathing.com" target="_blank" rel="noopener noreferrer" className="block">
                <div className="flex flex-col items-center p-2 bg-background/60 rounded-xl hover:bg-background/80 transition-colors text-center">
                  <span className="text-xl mb-1">🌬️</span>
                  <p className="text-[10px] font-medium text-primary">Breathwork</p>
                  <p className="text-[8px] text-muted-foreground">Healing</p>
                </div>
              </a>
              <a href="https://naspora.com/collections/health-wellness" target="_blank" rel="noopener noreferrer" className="block">
                <div className="flex flex-col items-center p-2 bg-background/60 rounded-xl hover:bg-background/80 transition-colors text-center">
                  <span className="text-xl mb-1">🌿</span>
                  <p className="text-[10px] font-medium text-primary">Naspora</p>
                  <p className="text-[8px] text-muted-foreground">Wellness</p>
                </div>
              </a>
            </div>
            <div className="grid grid-cols-4 gap-2">
              <a href="https://blackvegansrock.com" target="_blank" rel="noopener noreferrer" className="block">
                <div className="flex flex-col items-center p-2 bg-background/60 rounded-xl hover:bg-background/80 transition-colors text-center">
                  <span className="text-xl mb-1">🌱</span>
                  <p className="text-[10px] font-medium text-primary">Black Vegans</p>
                  <p className="text-[8px] text-muted-foreground">Plant-Based</p>
                </div>
              </a>
              <a href="https://healthfitnessrevolution.com" target="_blank" rel="noopener noreferrer" className="block">
                <div className="flex flex-col items-center p-2 bg-background/60 rounded-xl hover:bg-background/80 transition-colors text-center">
                  <span className="text-xl mb-1">✨</span>
                  <p className="text-[10px] font-medium text-primary">HFR Wellness</p>
                  <p className="text-[8px] text-muted-foreground">Holistic</p>
                </div>
              </a>
              <a href="https://blackgirlsrun.com" target="_blank" rel="noopener noreferrer" className="block">
                <div className="flex flex-col items-center p-2 bg-background/60 rounded-xl hover:bg-background/80 transition-colors text-center">
                  <span className="text-xl mb-1">🏃🏾‍♀️</span>
                  <p className="text-[10px] font-medium text-primary">BGR!</p>
                  <p className="text-[8px] text-muted-foreground">Community</p>
                </div>
              </a>
              <a href="https://therapyforblackgirls.com" target="_blank" rel="noopener noreferrer" className="block">
                <div className="flex flex-col items-center p-2 bg-background/60 rounded-xl hover:bg-background/80 transition-colors text-center">
                  <span className="text-xl mb-1">💜</span>
                  <p className="text-[10px] font-medium text-primary">Therapy</p>
                  <p className="text-[8px] text-muted-foreground">Mental Health</p>
                </div>
              </a>
            </div>
          </CardContent>
        </Card>

        {/* Manifestival Events Section */}
        <Link to="/events">
          <Card className="bg-gradient-to-r from-rose-500/10 via-pink-500/10 to-orange-500/10 border-rose-500/30 animate-fade-in mb-6 hover:shadow-lg transition-all cursor-pointer" data-testid="manifestival-events">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-rose-400 via-pink-500 to-orange-400 flex items-center justify-center shadow-lg">
                  <Sparkles className="h-5 w-5 text-white" />
                </div>
                <div>
                  <h3 className="text-sm font-bold text-primary">Manifestival ✨</h3>
                  <p className="text-xs text-muted-foreground">Share Good Energy With Me</p>
                </div>
              </div>
              
              <div className="bg-gradient-to-br from-rose-100 via-pink-50 to-orange-100 dark:from-rose-900/30 dark:via-pink-900/20 dark:to-orange-900/30 rounded-xl p-4 mb-3 border border-rose-200/50 dark:border-rose-500/20">
                <div className="flex flex-col sm:flex-row items-center gap-4">
                  <div className="flex-shrink-0 w-20 h-20 rounded-xl bg-gradient-to-br from-rose-400 to-orange-400 flex items-center justify-center shadow-lg">
                    <span className="text-4xl">☀️</span>
                  </div>
                  <div className="flex-1 text-center sm:text-left">
                    <span className="text-[10px] bg-rose-500 text-white px-2 py-0.5 rounded-full mb-2 inline-block">By Rooted Wellness</span>
                    <h4 className="text-base font-bold text-primary mb-1">The Good Vibes Experience</h4>
                    <p className="text-xs text-muted-foreground mb-2">
                      Bringing the Black community together through holistic wellness events featuring mindful artists, healthy food vendors, healers, conscious musicians & educators.
                    </p>
                    <div className="flex flex-wrap gap-2 justify-center sm:justify-start">
                      <span className="text-[10px] bg-background/60 px-2 py-1 rounded-full">🤝🏾 Community</span>
                      <span className="text-[10px] bg-background/60 px-2 py-1 rounded-full">🌿 Holistic Healing</span>
                      <span className="text-[10px] bg-background/60 px-2 py-1 rounded-full">✨ Good Vibes</span>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="flex items-center justify-between">
                <p className="text-xs text-muted-foreground">
                  <span className="font-medium text-rose-500">Starting in Detroit</span> • Coming to a city near you
                </p>
                <Button variant="outline" size="sm" className="rounded-full border-rose-500/30 text-rose-600 hover:bg-rose-500/10">
                  Learn More →
                </Button>
              </div>
            </CardContent>
          </Card>
        </Link>

        {/* Weekly Challenges - Both Angel Numbers and Spirit Animals for premium users */}
        {user?.is_premium && weeklyChallenges && (
          <div className="grid grid-cols-2 gap-4 animate-fade-in mb-6">
            {/* Angel Numbers Challenge */}
            <Link to="/angel-numbers" className="block">
              <Card className="h-full bg-gradient-to-br from-purple-50 to-indigo-50 dark:from-purple-500/10 dark:to-indigo-500/10 border-purple-200/50 dark:border-purple-500/30 hover:border-purple-300 dark:hover:border-purple-500/50 hover:shadow-md transition-all duration-300 cursor-pointer">
                <CardContent className="p-4 md:p-5 flex flex-col h-full">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <Target className="h-4 w-4 text-purple-500" />
                      <span className="text-[10px] md:text-xs uppercase tracking-wider text-purple-600 font-medium">Weekly Challenge</span>
                    </div>
                    <Star className="h-4 w-4 text-purple-400" />
                  </div>
                  <h3 className="text-base md:text-lg font-semibold text-primary mb-1">Angel Numbers</h3>
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-2xl md:text-3xl font-bold text-purple-500">{weeklyChallenges.angel.sightings_count}</span>
                    <span className="text-xs text-muted-foreground">this week</span>
                  </div>
                  {weeklyChallenges.angel.next_tier ? (
                    <div className="mt-auto">
                      <p className="text-[10px] md:text-xs text-muted-foreground mb-1">
                        {weeklyChallenges.angel.next_tier.sightings_needed} more for {weeklyChallenges.angel.next_tier.icon}
                      </p>
                      <div className="h-1.5 bg-purple-200 dark:bg-purple-900/30 rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-purple-500 transition-all" 
                          style={{ width: `${Math.min(100, (weeklyChallenges.angel.sightings_count / weeklyChallenges.angel.next_tier.sightings_required) * 100)}%` }}
                        />
                      </div>
                    </div>
                  ) : (
                    <p className="text-xs text-purple-500 font-medium mt-auto">🎉 Complete!</p>
                  )}
                </CardContent>
              </Card>
            </Link>

            {/* Spirit Animals Challenge */}
            <Link to="/spirit-animals" className="block">
              <Card className="h-full bg-gradient-to-br from-emerald-50 to-teal-50 dark:from-emerald-500/10 dark:to-teal-500/10 border-emerald-200/50 dark:border-emerald-500/30 hover:border-emerald-300 dark:hover:border-emerald-500/50 hover:shadow-md transition-all duration-300 cursor-pointer">
                <CardContent className="p-4 md:p-5 flex flex-col h-full">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <Flame className="h-4 w-4 text-emerald-500" />
                      <span className="text-[10px] md:text-xs uppercase tracking-wider text-emerald-600 font-medium">Weekly Challenge</span>
                    </div>
                    <PawPrint className="h-4 w-4 text-emerald-400" />
                  </div>
                  <h3 className="text-base md:text-lg font-semibold text-primary mb-1">Spirit Animals</h3>
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-2xl md:text-3xl font-bold text-emerald-500">{weeklyChallenges.spirit.sightings_count}</span>
                    <span className="text-xs text-muted-foreground">this week</span>
                  </div>
                  {weeklyChallenges.spirit.next_tier ? (
                    <div className="mt-auto">
                      <p className="text-[10px] md:text-xs text-muted-foreground mb-1">
                        {weeklyChallenges.spirit.next_tier.sightings_needed} more for {weeklyChallenges.spirit.next_tier.icon}
                      </p>
                      <div className="h-1.5 bg-emerald-200 dark:bg-emerald-900/30 rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-emerald-500 transition-all" 
                          style={{ width: `${Math.min(100, (weeklyChallenges.spirit.sightings_count / weeklyChallenges.spirit.next_tier.sightings_required) * 100)}%` }}
                        />
                      </div>
                    </div>
                  ) : (
                    <p className="text-xs text-emerald-500 font-medium mt-auto">🎉 Complete!</p>
                  )}
                </CardContent>
              </Card>
            </Link>
          </div>
        )}

        {/* Upgrade CTA for Free Users */}
        {!user?.is_premium && (
          <Link to="/subscription" className="block mt-8 md:mt-12 animate-fade-in">
            <Card className="bg-gradient-to-br from-violet-50 to-amber-50 dark:from-violet-500/10 dark:to-amber-500/10 border-violet-200/30 dark:border-border/50 hover:shadow-lg hover:border-violet-300 dark:hover:border-violet-500/50 transition-all cursor-pointer">
              <CardContent className="p-6 md:p-8 text-center">
                <Crown className="h-10 w-10 md:h-12 md:w-12 text-accent mx-auto mb-3 md:mb-4" />
                <h3 className="text-xl md:text-2xl text-primary mb-2">Choose Your Spiritual Path</h3>
                <p className="text-muted-foreground mb-6 max-w-md mx-auto">
                  Spiritually aligned pricing with angel numbers. Get unlimited journal, Sound Sanctuary, guided meditations, and more.
                </p>
                <div className="flex flex-wrap justify-center gap-6 mb-6">
                  <div className="text-center">
                    <p className="text-emerald-500 font-semibold">Seedling</p>
                    <p className="text-sm text-muted-foreground">Free</p>
                    <p className="text-xs text-emerald-500/70">Start Here</p>
                  </div>
                  <div className="text-center relative pt-5">
                    <span className="absolute -top-1 left-1/2 -translate-x-1/2 text-[10px] bg-violet-500 text-white px-2 py-0.5 rounded-full whitespace-nowrap">Popular</span>
                    <p className="text-violet-500 font-semibold">Rooted</p>
                    <p className="text-sm text-muted-foreground">$5.55/mo</p>
                    <p className="text-xs text-violet-500/70">Angel 555</p>
                  </div>
                  <div className="text-center">
                    <p className="text-amber-500 font-semibold">Forever Rooted</p>
                    <p className="text-sm text-muted-foreground">$111.11</p>
                    <p className="text-xs text-amber-500/70">Angel 1111</p>
                  </div>
                </div>
                <Button 
                  className="bg-accent text-accent-foreground rounded-full px-8 hover:bg-accent/90"
                  data-testid="dashboard-upgrade-btn"
                >
                  Choose Your Path →
                </Button>
              </CardContent>
            </Card>
          </Link>
        )}

        {/* Merch Collaboration Section */}
        <MerchCollabSection />

        {/* Stay Rooted Footer */}
        <div className="text-center py-8 animate-fade-in">
          <p className="text-sm text-muted-foreground italic">Stay Rooted 🌳</p>
        </div>
      </main>

      {/* Upgrade Modal */}
      <UpgradeModal 
        isOpen={upgradeModal.isOpen}
        onClose={() => setUpgradeModal({ isOpen: false, feature: "", tier: "awakening" })}
        feature={upgradeModal.feature}
        recommendedTier={upgradeModal.tier}
      />

      {/* Journey Unlock Prompt - Shows earned discounts */}
      {!user?.is_premium && activePrompt && (
        <JourneyUnlockPrompt 
          prompt={activePrompt}
          onClose={dismissPrompt}
        />
      )}

      {/* Soul Connection Quiz Modal */}
      <SoulConnectionQuiz 
        isOpen={showSoulQuizModal}
        onComplete={(result) => {
          setSoulConnectionQuiz(result);
          setShowSoulQuizModal(false);
          toast.success(`Your connection type: ${result.title} ${result.emoji}`);
        }}
        onClose={() => setShowSoulQuizModal(false)}
      />

      {/* Floating Vibe Out Button — anchored bottom-LEFT, offset slightly
          inward so it doesn't feel rigidly glued to the corner. Paired with
          a slow drift animation for whimsy. */}
      <button
        onClick={() => setShowVibeOutHub(true)}
        className="fixed bottom-5 left-6 z-40 w-14 h-14 rounded-full bg-gradient-to-br from-violet-500 via-purple-500 to-pink-500 text-white transition-transform flex items-center justify-center group hover:scale-110"
        style={{ animation: 'float-drift-1 7s ease-in-out infinite, soft-glow 3.5s ease-in-out infinite' }}
        data-testid="vibe-out-fab"
        title="Vibe Out - Stress Relief"
        aria-label="Vibe Out — quick stress relief"
      >
        <span className="text-2xl group-hover:animate-pulse" aria-hidden="true">✨</span>
        <span className="absolute -top-1 -right-1 w-3 h-3 bg-amber-400 rounded-full animate-pulse" />
      </button>

      {/* Vibe Out Hub Modal */}
      <VibeOutHub
        isOpen={showVibeOutHub}
        onClose={() => setShowVibeOutHub(false)}
      />
      
      {/* Weekly Progress Report Modal */}
      <WeeklyProgressReport
        isOpen={showWeeklyReport}
        onClose={() => setShowWeeklyReport(false)}
      />
      
      {/* Moon & Cycle Tracker Modal */}
      <MoonCycleTracker
        isOpen={showMoonCycle}
        onClose={() => setShowMoonCycle(false)}
      />
      
      {/* Rooted Manifestation Miracle Deck Modal */}
      <MiracleDeck
        isOpen={showMiracleDeck}
        onClose={() => setShowMiracleDeck(false)}
      />
      
      {/* Healing Together (Soul Bond Mode) Modal */}
      <HealingTogether
        isOpen={showHealingTogether}
        onClose={() => setShowHealingTogether(false)}
      />
      
      {/* Manifestation Circles Modal */}
      <ManifestationCircles
        isOpen={showManifestationCircles}
        onClose={() => setShowManifestationCircles(false)}
      />
      
      {/* Notification Settings Modal */}
      {showNotificationSettings && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-start sm:items-center justify-center p-2 sm:p-4 pt-12 sm:pt-4 overflow-y-auto">
          <NotificationSettings onClose={() => setShowNotificationSettings(false)} />
        </div>
      )}
    </div>
  );
};

export default Dashboard;
